import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User } from '../types/auth';
import InteractiveSlider from './InteractiveSlider';
import DisclaimerModal from './DisclaimerModal';
import GuidanceModal from './GuidanceModal';
import GoogleAdInterstitial from './GoogleAdInterstitial';
import StructuredData from './StructuredData';
import HousingPDFService from '../services/housing-pdf-service';
import housingScenariosService from '../services/housing-scenarios';
import HousingSavedScenarios from './HousingSavedScenarios';
import {
  HousingComparisonData,
  SavedHousingScenario,
  MonthlyCashFlow,
  PurchaseSummary,
  RentalAnalysis,
  RentalFundFlow
} from '../types/housingComparison';
import { calculateOwnerSummary, calculateRenterSummary, buildNetWorthByYear, findCrossover, findBuyerBreakEvenYear, findBreakEvenSavingsDiscipline, discountToTodaysDollars, simulateInvestmentFund, Crossover } from './housing/rentVsBuyEngine';
import { capitalGainsTaxRate, defaultMarginalTaxRate, US_CAPITAL_GAINS_TAX_RATE, US_STANDARD_DEDUCTION_SINGLE, US_SALT_CAP } from './housing/housingCalculations';
import { CITY_PRESETS, CITY_PRESET_AS_OF, CITY_PRESET_SOURCE, CityPreset } from './housing/cityPresets';
import { buildLandlordCashFlows, simulateLandlord, findLandlordBreakEvenRent, LandlordParams } from './housing/landlordEngine';
import { HOUSING_TEMPLATES } from './housing/housingTemplates';
import NetWorthRaceChart from './housing/NetWorthRaceChart';
import { BaseScenarioData } from '../types/shared';
import { TylerState, updateReact, useTylerStateUpdate } from '../utils/TylerState';
import { debugLog } from '../utils/debugLogger';
import { formatCurrencyWithColor as formatCurrencyWithColorShared } from '../utils/inputFormatters';
import { useCountry } from '../contexts/CountryContext';
import { getStatePropertyTax } from '../utils/usStateTaxRates';

interface HousingCalculatorProps {
  user?: any;
  guestScenario?: any;
  onSaveScenario?: (data: any) => void;
}

type AnalysisMode = 'housing-to-rent' | 'rent-to-housing' | 'compare';

// Use the shared HousingComparisonData type
type PurchaseInputs = HousingComparisonData;


/** How far past the user's hold period we look for the year buying starts to pay. */
const LONG_HORIZON_YEARS = 40;

const HousingCalculator: React.FC<HousingCalculatorProps> = ({ user, guestScenario, onSaveScenario }) => {
  // Subscribe to TylerState updates
  useTylerStateUpdate();

  // Get country from global context
  const { isUSA, countryName } = useCountry();

  // Set page title and meta description for SEO - Enhanced with keywords
  useEffect(() => {
    const region = isUSA ? 'USA' : 'Canada';
    if (isUSA) {
      document.title = `Rent vs Buy Calculator USA 2026 | Compare Mortgage & PMI Costs | Larry's Help`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Free rent vs buy calculator for USA. Compare mortgage payments, PMI, property taxes, tax deductions, and investment returns. See your break-even point and make informed housing decisions with our detailed analysis.`);
      }
    } else {
      document.title = `Rent vs Buy Calculator Canada 2026 | CMHC, Land Transfer Tax | Larry's Help`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Free rent vs buy calculator for Canada. Includes CMHC insurance, land transfer tax rebates for first-time buyers, and the opportunity cost of your down payment. Compare Toronto, Vancouver, and all provinces. See your break-even point.`);
      }
    }
  }, [isUSA]);

  const [inputs, setInputs] = useState<PurchaseInputs>({
    region: 'Canada',
    analysisMode: 'compare',
    isLandlord: false,
    ownershipYears: 10,
    houseCost: 500000,
    closingCostsPercent: 3,
    downpaymentAmount: 100000,
    downpaymentPercent: 20,
    useDownpaymentPercent: false,
    mortgageYears: 30,
    interestRate: 5,
    propertyTaxPercent: 1,
    maintenancePercent: 1,
    condoFees: 0,
    annualAppreciation: 3,
    sellingCostsPercent: 7,
    taxesPercent: 0,
    monthlyRent: 2500,
    propertyManagementFeePercent: 8,
    vacancyRatePercent: 5,
    investmentReturnRate: 5,
    rentalInflationRate: 3,
    investmentTaxRate: 0,
    marginalTaxRate: 40,
    renterSavingsDisciplinePercent: 100,
    inflationRate: 3,
    extraMonthlyPayment: 0,
    annualHomeInsurance: 1200
  });

  // Detailed closing costs toggle (like car calculator)
  const [useDetailedClosingCosts, setUseDetailedClosingCosts] = useState(false);
  const [detailedClosingCosts, setDetailedClosingCosts] = useState({
    landTransferTax: 0,
    legalFees: 2000,
    homeInspection: 600,
    appraisal: 400,
    titleInsurance: 300,
    movingCosts: 1500
  });



  const [summary, setSummary] = useState<PurchaseSummary | null>(null);
  const [rentalAnalysis, setRentalAnalysis] = useState<RentalAnalysis | null>(null);
  const [equivalentHomeValue, setEquivalentHomeValue] = useState<number | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showRentalDetails, setShowRentalDetails] = useState(false);

  const [waterfallView, setWaterfallView] = useState<'monthly' | 'annual' | 'total'>('monthly');
  const [showAllMonths, setShowAllMonths] = useState(false);
  const [selectedDetailView, setSelectedDetailView] = useState<'financing' | 'sale' | 'cashflow' | 'monthly' | 'rental' | null>(null);
  const [monthlyBreakdownView, setMonthlyBreakdownView] = useState<'summary' | 'detail'>('summary');
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [activeCityPreset, setActiveCityPreset] = useState<string | null>(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [guidanceTitle, setGuidanceTitle] = useState('');
  const [guidanceContent, setGuidanceContent] = useState('');
  const [showStickyCalculateButton, setShowStickyCalculateButton] = useState(false);
  
  // Navigation state
  const [activeSection, setActiveSection] = useState('analysisType');
  const [isInputSectionVisible, setIsInputSectionVisible] = useState(true);
  const [pendingSaveAfterLogin, setPendingSaveAfterLogin] = useState(false);

  // First-time buyer state (for land transfer tax rebates)
  const [isFirstTimeBuyer, setIsFirstTimeBuyer] = useState(false);
  const [buyerLocation, setBuyerLocation] = useState<string>('ON'); // Province/State

  // US Tax Deduction settings
  const [useTaxDeductions, setUseTaxDeductions] = useState(true);
  // Saved scenarios from before this field existed fall back to the regional default
  const marginalTaxRate = inputs.marginalTaxRate ?? defaultMarginalTaxRate(inputs.region);

  // Auto-save timeout ref (same pattern as car calculator)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to handle automatic save after login (moved after function definition)

  const handleInputChange = (field: keyof PurchaseInputs, value: number | boolean | string) => {
    setInputs(prev => {
      const newInputs = { ...prev, [field]: value };
      const gainsRate = capitalGainsTaxRate(newInputs.marginalTaxRate ?? defaultMarginalTaxRate(newInputs.region), newInputs.region);

      // Landlord mode sells an investment property, so capital gains tax applies; a principal residence is exempt
      if (field === 'analysisMode') {
        const isLandlordMode = (value === 'landlord-breakeven');
        newInputs.isLandlord = isLandlordMode;
        newInputs.taxesPercent = isLandlordMode ? gainsRate : 0;

        // Reset inputs that become hidden in the new mode to sensible defaults
        // so prior scenario values don't silently persist
        if (value === 'rent-to-housing') {
          newInputs.houseCost = 500000;
          newInputs.downpaymentAmount = 100000;
          newInputs.downpaymentPercent = 20;
        }
      }

      // Legacy: Auto-adjust capital gains tax when switching between Occupy and Landlord
      // (kept for backward compatibility with saved scenarios)
      if (field === 'isLandlord') {
        newInputs.taxesPercent = value ? gainsRate : 0;
      }

      // A new marginal rate changes the effective rate of any tax that is switched on
      if (field === 'marginalTaxRate') {
        if (newInputs.taxesPercent > 0) newInputs.taxesPercent = gainsRate;
        if (newInputs.investmentTaxRate > 0) newInputs.investmentTaxRate = gainsRate;
      }

      return newInputs;
    });

    // Clear results when any input changes (consistent behavior)
    setSummary(null);
    setRentalAnalysis(null);
    setEquivalentHomeValue(null);
    setShowResults(false);
    setSelectedDetailView(null);

    // Debounced auto-save: trigger auto-save 2 seconds after user stops typing
    // Only save if user has entered a scenario name
    if (TylerState.user && TylerState.housingName && TylerState.housingName.trim()) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveScenario();
      }, 2000); // 2 second debounce
    }
  };

  // Scroll handler for sticky calculate button
  useEffect(() => {
    const handleScroll = () => {
      const calculateButton = document.querySelector('.form-actions');
      if (calculateButton) {
        const rect = calculateButton.getBoundingClientRect();
        // Show sticky button when main button is out of view (scrolled past)
        setShowStickyCalculateButton(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sync with global country context - update region and defaults when country changes
  useEffect(() => {
    const newRegion: 'Canada' | 'US' = isUSA ? 'US' : 'Canada';
    // US has higher average property tax (1.1%) vs Canada (1%)
    // US has no land transfer tax (baked into closing costs)
    // US closing costs are typically 2-5%
    const newPropertyTaxRate = isUSA ? 1.1 : 1.0;
    const newClosingCosts = isUSA ? 3.0 : 3.0; // Similar baseline

    setInputs(prev => {
      const marginalTaxRate = defaultMarginalTaxRate(newRegion);
      const gainsRate = capitalGainsTaxRate(marginalTaxRate, newRegion);
      return {
        ...prev,
        region: newRegion,
        propertyTaxPercent: newPropertyTaxRate,
        closingCostsPercent: newClosingCosts,
        marginalTaxRate,
        taxesPercent: prev.taxesPercent > 0 ? gainsRate : 0,
        investmentTaxRate: prev.investmentTaxRate > 0 ? gainsRate : 0,
      };
    });
  }, [isUSA]);

  // Calculate total ownership years from stages
  const getTotalOwnershipYears = () => {
    return inputs.ownershipYears;
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape key to close modals or clear selections
    if (e.key === 'Escape') {
      if (showDisclaimerModal) {
        setShowDisclaimerModal(false);
      }
      if (selectedDetailView) {
        setSelectedDetailView(null);
      }
    }
    
    // Enter key to calculate when focused on calculate button
    if (e.key === 'Enter' && e.target === e.currentTarget) {
      handleCalculate();
    }
  };

  const calculateMonthlyMortgagePayment = (principal: number, annualRate: number, years: number): number => {
    if (years <= 0) return 0;
    const monthlyRate = annualRate / 100 / 12;
    const numberOfPayments = years * 12;

    if (monthlyRate === 0) return principal / numberOfPayments;

    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
           (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  };

  // Calculate Land Transfer Tax and First-Time Buyer Rebates (Canada)
  const calculateLandTransferTax = (purchasePrice: number, province: string, isFirstTime: boolean) => {
    let provincialTax = 0;
    let municipalTax = 0; // Toronto has municipal LTT
    let rebate = 0;

    if (inputs.region === 'US') {
      // USA doesn't have land transfer tax (transfer taxes vary by state but are typically much lower)
      return { provincialTax: 0, municipalTax: 0, rebate: 0, netTax: 0 };
    }

    // Ontario Land Transfer Tax (tiered)
    if (province === 'ON') {
      if (purchasePrice <= 55000) {
        provincialTax = purchasePrice * 0.005;
      } else if (purchasePrice <= 250000) {
        provincialTax = 275 + (purchasePrice - 55000) * 0.01;
      } else if (purchasePrice <= 400000) {
        provincialTax = 2225 + (purchasePrice - 250000) * 0.015;
      } else if (purchasePrice <= 2000000) {
        provincialTax = 4475 + (purchasePrice - 400000) * 0.02;
      } else {
        provincialTax = 36475 + (purchasePrice - 2000000) * 0.025;
      }

      // First-time buyer rebate (Ontario): Up to $4,000
      if (isFirstTime) {
        rebate = Math.min(provincialTax, 4000);
      }
    }
    // BC Property Transfer Tax (tiered)
    else if (province === 'BC') {
      if (purchasePrice <= 200000) {
        provincialTax = purchasePrice * 0.01;
      } else if (purchasePrice <= 2000000) {
        provincialTax = 2000 + (purchasePrice - 200000) * 0.02;
      } else if (purchasePrice <= 3000000) {
        provincialTax = 38000 + (purchasePrice - 2000000) * 0.03;
      } else {
        provincialTax = 68000 + (purchasePrice - 3000000) * 0.05;
      }

      // First-time buyer exemption (BC): Full exemption up to $500K, partial up to $525K
      if (isFirstTime) {
        if (purchasePrice <= 500000) {
          rebate = provincialTax; // Full exemption
        } else if (purchasePrice <= 525000) {
          // Partial exemption: rebate decreases proportionally
          const exemptionReduction = (purchasePrice - 500000) / 25000;
          const maxExemption = 8000; // Tax on $500K home
          rebate = maxExemption * (1 - exemptionReduction);
        }
      }
    }
    // Quebec Welcome Tax (tiered)
    else if (province === 'QC') {
      if (purchasePrice <= 55200) {
        provincialTax = purchasePrice * 0.005;
      } else if (purchasePrice <= 276200) {
        provincialTax = 276 + (purchasePrice - 55200) * 0.01;
      } else if (purchasePrice <= 552300) {
        provincialTax = 2486 + (purchasePrice - 276200) * 0.015;
      } else {
        provincialTax = 6627.50 + (purchasePrice - 552300) * 0.02;
      }
      // Quebec has no first-time buyer rebate for welcome tax
    }
    // Other provinces (flat rates or no LTT)
    else if (['AB', 'SK'].includes(province)) {
      // Alberta and Saskatchewan have no land transfer tax
      provincialTax = 0;
    } else if (province === 'MB') {
      // Manitoba: Tiered from 0% to 2%
      if (purchasePrice <= 30000) {
        provincialTax = 0;
      } else if (purchasePrice <= 90000) {
        provincialTax = (purchasePrice - 30000) * 0.005;
      } else if (purchasePrice <= 150000) {
        provincialTax = 300 + (purchasePrice - 90000) * 0.01;
      } else if (purchasePrice <= 200000) {
        provincialTax = 900 + (purchasePrice - 150000) * 0.015;
      } else {
        provincialTax = 1650 + (purchasePrice - 200000) * 0.02;
      }
    } else {
      // Default: estimate 1% for other provinces
      provincialTax = purchasePrice * 0.01;
    }

    // Toronto Municipal Land Transfer Tax (same rates as Ontario provincial)
    if (buyerLocation === 'Toronto' || buyerLocation === 'ON-Toronto') {
      if (purchasePrice <= 55000) {
        municipalTax = purchasePrice * 0.005;
      } else if (purchasePrice <= 250000) {
        municipalTax = 275 + (purchasePrice - 55000) * 0.01;
      } else if (purchasePrice <= 400000) {
        municipalTax = 2225 + (purchasePrice - 250000) * 0.015;
      } else if (purchasePrice <= 2000000) {
        municipalTax = 4475 + (purchasePrice - 400000) * 0.02;
      } else {
        municipalTax = 36475 + (purchasePrice - 2000000) * 0.025;
      }

      // Toronto first-time buyer rebate: Up to $4,475
      if (isFirstTime) {
        rebate += Math.min(municipalTax, 4475);
      }
    }

    const netTax = Math.max(0, provincialTax + municipalTax - rebate);

    return { provincialTax, municipalTax, rebate, netTax };
  };

  // Auto-update detailed closing costs LTT when province or house price changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (inputs.region === 'Canada' && useDetailedClosingCosts) {
      const ltt = calculateLandTransferTax(inputs.houseCost, buyerLocation.replace('-Toronto', ''), false);
      setDetailedClosingCosts(prev => ({ ...prev, landTransferTax: ltt.netTax }));
    }
  }, [buyerLocation, inputs.houseCost, inputs.region, useDetailedClosingCosts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate US Mortgage Interest Deduction (Tax Benefit)
  const calculateMortgageInterestDeduction = (cashFlows: MonthlyCashFlow[], taxRate: number) => {
    if (inputs.region !== 'US' || !useTaxDeductions) {
      return { annualInterest: [], annualDeduction: [], totalDeduction: 0, totalSavings: 0 };
    }

    // US mortgage interest deduction caps (2018 TCJA):
    // - Mortgage debt limit: $750,000 for new loans
    // - Property tax deduction (SALT): capped at US_SALT_CAP ($40K for 2025-2029, $10K from 2030)
    const mortgageDebtLimit = 750000;
    const loanAmount = cashFlows.length > 0 ? cashFlows[0].remainingBalance + cashFlows[0].principalPaid : 0;
    const deductibleRatio = Math.min(1, mortgageDebtLimit / loanAmount);

    const annualInterest: number[] = [];
    const annualDeduction: number[] = [];
    let totalDeduction = 0;
    let totalSavings = 0;

    // Group by year
    const years = getTotalOwnershipYears();
    for (let year = 1; year <= years; year++) {
      const yearCashFlows = cashFlows.filter(cf => cf.year === year);
      const yearInterest = yearCashFlows.reduce((sum, cf) => sum + cf.interestPaid, 0);
      const yearPropertyTax = yearCashFlows.reduce((sum, cf) => sum + cf.propertyTax, 0);

      // Apply mortgage debt limit ratio to interest
      const deductibleInterest = yearInterest * deductibleRatio;

      // Property tax deduction capped at the SALT cap
      const deductiblePropertyTax = Math.min(yearPropertyTax, US_SALT_CAP);

      // Total itemized deduction for this year
      const yearDeduction = deductibleInterest + deductiblePropertyTax;

      annualInterest.push(yearInterest);
      annualDeduction.push(yearDeduction);
      totalDeduction += yearDeduction;
      // Itemizing only beats the standard deduction on the amount above it
      totalSavings += Math.max(0, yearDeduction - US_STANDARD_DEDUCTION_SINGLE) * (taxRate / 100);
    }

    return {
      annualInterest,
      annualDeduction,
      totalDeduction,
      totalSavings,
      deductibleRatio
    };
  };

  const calculateRentalAnalysis = (purchaseSummary: PurchaseSummary, useInputRent = false): RentalAnalysis => {
    const renter = calculateRenterSummary(inputs, purchaseSummary, useInputRent);
    if (!useInputRent) {
      TylerState.housing.outputs.comparison = { ...TylerState.housing.outputs.comparison, rentIterations: renter.iterations };
    }
    return renter;
  };

  // Reverse calculation: Find equivalent home value for given monthly rent
  const calculateEquivalentHomeValue = (_targetMonthlyRent: number): number => {
    // 3-phase linear search for home value where net proceeds = rental fund balance
    // Phase 1: $100k steps until direction flips
    // Phase 2: $10k steps until direction flips
    // Phase 3: $1k steps until within $1k tolerance
    // Note: Monthly rent is read from inputs.monthlyRent by calculateRentalAnalysis

    let testHomeValue = 500000; // Starting guess: $500k
    let totalIterations = 0;
    const originalHouseCost = inputs.houseCost;

    // Helper function to calculate difference (netProceeds - rentalFundBalance) for a given home value
    const calculateDifference = (homeValue: number): number => {
      inputs.houseCost = homeValue;
      const testPurchase = calculatePurchaseSummary();
      const netProceeds = testPurchase.netProceedsFromSale;
      const testRental = calculateRentalAnalysis(testPurchase, true);
      const rentalFundBalance = testRental.finalFundBalance;
      return netProceeds - rentalFundBalance;
    };

    // Phase 1: $100k steps
    let step = 100000;
    let lastDiff = calculateDifference(testHomeValue);
    let lastHomeValue = testHomeValue;

    while (totalIterations < 500) {
      totalIterations++;

      // If netProceeds > fundBalance, we need a MORE expensive home (increase proceeds)
      // If netProceeds < fundBalance, we need a CHEAPER home (decrease proceeds)
      testHomeValue += (lastDiff > 0) ? step : -step;
      const newDiff = calculateDifference(testHomeValue);

      // Check for direction flip (sign change)
      if ((lastDiff > 0) !== (newDiff > 0)) {
        testHomeValue = lastHomeValue; // Go back to before flip
        break;
      }

      lastDiff = newDiff;
      lastHomeValue = testHomeValue;
    }

    // Phase 2: $10k steps
    step = 10000;
    lastDiff = calculateDifference(testHomeValue);
    lastHomeValue = testHomeValue;

    while (totalIterations < 500) {
      totalIterations++;

      testHomeValue += (lastDiff > 0) ? step : -step;
      const newDiff = calculateDifference(testHomeValue);

      // Check for direction flip
      if ((lastDiff > 0) !== (newDiff > 0)) {
        testHomeValue = lastHomeValue; // Go back to before flip
        break;
      }

      lastDiff = newDiff;
      lastHomeValue = testHomeValue;
    }

    // Phase 3: $1k steps until direction flips
    step = 1000;
    lastDiff = calculateDifference(testHomeValue);
    lastHomeValue = testHomeValue;

    while (totalIterations < 1000) {
      totalIterations++;

      testHomeValue += (lastDiff > 0) ? step : -step;
      const newDiff = calculateDifference(testHomeValue);

      // Check for direction flip
      if ((lastDiff > 0) !== (newDiff > 0)) {
        testHomeValue = lastHomeValue; // Go back to before flip
        break;
      }

      lastDiff = newDiff;
      lastHomeValue = testHomeValue;
    }

    // Phase 4: $100 steps until direction flips
    step = 100;
    lastDiff = calculateDifference(testHomeValue);
    lastHomeValue = testHomeValue;

    while (totalIterations < 1000) {
      totalIterations++;

      testHomeValue += (lastDiff > 0) ? step : -step;
      const newDiff = calculateDifference(testHomeValue);

      // Check for direction flip
      if ((lastDiff > 0) !== (newDiff > 0)) {
        testHomeValue = lastHomeValue; // Go back to before flip
        break;
      }

      lastDiff = newDiff;
      lastHomeValue = testHomeValue;
    }

    // Phase 5: $10 steps until direction flips
    step = 10;
    lastDiff = calculateDifference(testHomeValue);
    lastHomeValue = testHomeValue;

    while (totalIterations < 1000) {
      totalIterations++;

      testHomeValue += (lastDiff > 0) ? step : -step;
      const newDiff = calculateDifference(testHomeValue);

      // Check for direction flip
      if ((lastDiff > 0) !== (newDiff > 0)) {
        testHomeValue = lastHomeValue; // Go back to before flip
        break;
      }

      lastDiff = newDiff;
      lastHomeValue = testHomeValue;
    }

    // Phase 6: $1 steps until within $1 tolerance
    step = 1;
    let diff = calculateDifference(testHomeValue);

    while (Math.abs(diff) >= 1 && totalIterations < 1000) {
      totalIterations++;

      testHomeValue += (diff > 0) ? step : -step;
      const newDiff = calculateDifference(testHomeValue);

      // Check if we're oscillating
      if (Math.abs(newDiff) > Math.abs(diff)) {
        console.log(`⚠️ Phase 6 oscillating at iteration ${totalIterations}: value=$${testHomeValue.toFixed(0)}, diff went from $${diff.toFixed(2)} to $${newDiff.toFixed(2)}`);
        testHomeValue -= (diff > 0) ? step : -step; // Revert last step
        break;
      }

      diff = newDiff;
    }

    // Restore original house cost
    inputs.houseCost = originalHouseCost;

    console.log(`✅ 6-phase home value search converged in ${totalIterations} iterations: value=$${testHomeValue.toFixed(0)}, diff=$${diff.toFixed(2)}`);

    // Store iteration count in TylerState for debugging
    TylerState.housing.outputs.comparison = { ...TylerState.housing.outputs.comparison, homeValueIterations: totalIterations };

    return testHomeValue;
  };

  const calculatePurchaseSummary = (): PurchaseSummary => {
    // Calculate base closing costs
    let closingCosts = useDetailedClosingCosts
      ? Object.values(detailedClosingCosts).reduce((sum, val) => sum + val, 0)
      : inputs.houseCost * inputs.closingCostsPercent / 100;

    // Apply first-time buyer rebate (Canada only)
    if (isFirstTimeBuyer && inputs.region === 'Canada') {
      const ltt = calculateLandTransferTax(inputs.houseCost, buyerLocation.replace('-Toronto', ''), true);
      // If using detailed costs, the rebate reduces the land transfer tax component
      // If using simple %, we subtract the rebate from total closing costs
      closingCosts = Math.max(0, closingCosts - ltt.rebate);
    }

    return calculateOwnerSummary(inputs, closingCosts);
  };

  /**
   * Calculate break-even rent for landlords
   *
   * Compares two scenarios:
   * Case 1 (Buy as Landlord):
   *   - Buy property, collect rent, pay expenses
   *   - Final position = (property value - mortgage)
   *
   * Case 2 (Don't Buy - Invest Instead):
   *   - Invest downpayment in fund
   *   - Each month: add/subtract landlord's net cash flow to fund
   *   - Final position = investment fund balance
   *
   * Break-even rent = rent where both final positions are equal
   */
  /** Everything landlord-side needs beyond the purchase itself. */
  const landlordParams = (): Omit<LandlordParams, 'monthlyRent'> => ({
    rentalInflationRate: inputs.rentalInflationRate,
    propertyManagementFeePercent: inputs.propertyManagementFeePercent,
    vacancyRatePercent: inputs.vacancyRatePercent ?? 5,
    marginalTaxRate,
    investmentReturnRate: inputs.investmentReturnRate,
    investmentTaxRate: inputs.investmentTaxRate
  });

  const calculateLandlordBreakEven = (): number => {
    return Math.max(0, findLandlordBreakEvenRent(calculatePurchaseSummary(), landlordParams()).rent);
  };

  const calculateLandlordInvestmentFund = (purchaseSummary: PurchaseSummary, monthlyRent: number): RentalAnalysis => {
    const { cashFlows, fund, finalFundBalance } = simulateLandlord(purchaseSummary, { ...landlordParams(), monthlyRent });
    const seed = purchaseSummary.downpayment + purchaseSummary.closingCosts;

    return {
      equivalentMonthlyRent: monthlyRent,
      rentalFundFlows: fund.map((fundMonth, index) => ({
        month: index + 1,
        year: Math.floor(index / 12) + 1,
        openingBalance: fundMonth.openingBalance,
        initialFunding: index === 0 ? seed : 0,
        // Money into the fund, the same meaning this field carries on the rent-vs-buy side.
        // Negative means the property paid for itself and the investor took cash out instead.
        monthlyFunding: index === 0 ? fundMonth.netContribution - seed : fundMonth.netContribution,
        rentalPayment: cashFlows[index].rentalIncome,
        investmentIncome: fundMonth.investmentIncome,
        taxes: 0,
        closingBalance: fundMonth.closingBalance,
        closingBalanceAfterTax: fundMonth.closingBalanceAfterTax
      })),
      totalRentalPayments: cashFlows.reduce((total, flow) => total + flow.rentalIncome, 0),
      totalInvestmentIncome: fund.reduce((total, fundMonth) => total + fundMonth.investmentIncome, 0),
      capitalGainsTax: fund[fund.length - 1]?.taxIfSoldNow ?? 0,
      finalFundBalance
    };
  };

  /** Rental income, fees, vacancy and tax layered onto the ownership cash flows. */
  const enhanceCashFlowsWithLandlordData = (purchaseSummary: PurchaseSummary, monthlyRent: number): MonthlyCashFlow[] =>
    buildLandlordCashFlows(purchaseSummary.monthlyCashFlows, { ...landlordParams(), monthlyRent });

  const handleCalculate = () => {
    // Only show disclaimer on first calculation per session
    if (disclaimerAccepted) {
      // Supporters: never. Logged-in: 20% chance. Guests: always.
      const currentUser = TylerState.user;
      const isSupporter = currentUser && currentUser.tier === 'supporter';
      const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
      if (showAd) {
        setShowAdModal(true);
      } else {
        performCalculation();
      }
      return;
    }
    setShowDisclaimerModal(true);
  };

  const handleDisclaimerAccept = () => {
    setShowDisclaimerModal(false);
    setDisclaimerAccepted(true);

    // Supporters: never. Logged-in: 20% chance. Guests: always.
    const currentUser = TylerState.user;
    const isSupporter = currentUser && currentUser.tier === 'supporter';
    const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
    if (showAd) {
      setShowAdModal(true);
    } else {
      performCalculation();
    }
  };

  // Debounced auto-save on input change (same pattern as car calculator)
  // Removed debounced autosave - Tyler wants save only on: calculate, manual save, screen change

  // Auto-save scenario after calculate (same pattern as car calculator)
  const autoSaveScenario = async () => {
    const currentUser = TylerState.user;
    if (!currentUser) return;

    // Read from TylerState (single source of truth)
    const name = (TylerState.housingName || '').trim();
    const finalName = name || 'Temporary';

    debugLog('🔍 AUTO-SAVING HOUSING SCENARIO', { finalName });

    try {
      // Get existing scenarios to check if updating
      const scenariosArray = await housingScenariosService.getHousingScenariosArray();
      const existingScenario = scenariosArray.find(s => s.name.toLowerCase() === finalName.toLowerCase());

      if (existingScenario) {
        // Update existing scenario
        await housingScenariosService.updateHousingScenario(existingScenario.id, {
          name: finalName,
          description: '',
          data: inputs
        });
        debugLog('💾 SAVING HOUSING SCENARIO (UPDATE)', { name: finalName });
      } else {
        // Check tier limit for new scenarios
        const cap = getScenarioCap(currentUser.tier || 'free');
        if (scenariosArray.length >= cap) {
          console.log('⚠️ Scenario cap reached, not auto-saving');
          return;
        }

        // Create new scenario
        await housingScenariosService.saveHousingScenario({
          name: finalName,
          description: '',
          data: inputs
        });
        debugLog('✅ SAVING HOUSING SCENARIO (NEW)', { name: finalName });
      }
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  };

  const performCalculation = () => {
    // Save inputs to TylerState (single source of truth like car calculator)
    TylerState.housing.inputs = { ...inputs };

    // Normal calculation flow
    if (inputs.analysisMode === 'housing-to-rent') {
        // Mode A: Housing Cost → Rent
        const purchaseResult = calculatePurchaseSummary();
        const rentalResult = calculateRentalAnalysis(purchaseResult);
        setSummary(purchaseResult);
        setRentalAnalysis(rentalResult);
        setEquivalentHomeValue(null);

        // Save outputs to TylerState with enhanced metrics
        TylerState.housing.outputs.purchase = {
          ...purchaseResult,
          costPerMonth: purchaseResult.monthlyCost,
          costPerYear: purchaseResult.monthlyCost * 12,
          totalNetCost: purchaseResult.costBreakdown.netCost
        };
        TylerState.housing.outputs.rental = {
          ...rentalResult,
          costPerMonth: rentalResult.equivalentMonthlyRent,
          costPerYear: rentalResult.equivalentMonthlyRent * 12,
          totalNetCashFlow: rentalResult.finalFundBalance
        };
      } else if (inputs.analysisMode === 'rent-to-housing') {
        // Mode B: Rent → Housing Cost
        const calculatedHomeValue = calculateEquivalentHomeValue(inputs.monthlyRent);
        setEquivalentHomeValue(calculatedHomeValue);
        // Temporarily update house cost for calculation
        const originalHouseCost = inputs.houseCost;
        inputs.houseCost = calculatedHomeValue;
        const purchaseResult = calculatePurchaseSummary();
        const rentalResult = calculateRentalAnalysis(purchaseResult, true); // Use input rent, not calculated
        // Restore original house cost
        inputs.houseCost = originalHouseCost;
        setSummary(purchaseResult);
        setRentalAnalysis(rentalResult);

        // Save outputs to TylerState with enhanced metrics
        TylerState.housing.outputs.purchase = {
          ...purchaseResult,
          costPerMonth: purchaseResult.monthlyCost,
          costPerYear: purchaseResult.monthlyCost * 12,
          totalNetCost: purchaseResult.costBreakdown.netCost,
          equivalentHomeValue: calculatedHomeValue
        };
        TylerState.housing.outputs.rental = {
          ...rentalResult,
          costPerMonth: rentalResult.equivalentMonthlyRent,
          costPerYear: rentalResult.equivalentMonthlyRent * 12,
          totalNetCashFlow: rentalResult.finalFundBalance
        };
      } else if (inputs.analysisMode === 'compare') {
        // Mode C: Compare Both
        const purchaseResult = calculatePurchaseSummary();
        const rentalResult = calculateRentalAnalysis(purchaseResult);
        setSummary(purchaseResult);
        setRentalAnalysis(rentalResult);
        setEquivalentHomeValue(null);

        // Save outputs to TylerState with enhanced metrics
        TylerState.housing.outputs.purchase = {
          ...purchaseResult,
          costPerMonth: purchaseResult.monthlyCost,
          costPerYear: purchaseResult.monthlyCost * 12,
          totalNetCost: purchaseResult.costBreakdown.netCost
        };
        TylerState.housing.outputs.rental = {
          ...rentalResult,
          costPerMonth: rentalResult.equivalentMonthlyRent,
          costPerYear: rentalResult.equivalentMonthlyRent * 12,
          totalNetCashFlow: rentalResult.finalFundBalance
        };
        const renterAtActualRent = calculateRenterSummary(inputs, purchaseResult, true);
        TylerState.housing.outputs.comparison = {
          ...TylerState.housing.outputs.comparison,
          winner: renterAtActualRent.finalFundBalance > purchaseResult.netProceedsFromSale ? 'Renting' : 'Buying',
          difference: Math.abs(renterAtActualRent.finalFundBalance - purchaseResult.netProceedsFromSale)
        };
      } else if (inputs.analysisMode === 'landlord-breakeven') {
        // Mode D: Landlord Break-Even - What rent do I need to charge?
        const breakEvenRent = calculateLandlordBreakEven();
        setEquivalentHomeValue(breakEvenRent); // Reuse this state variable for break-even rent

        // Calculate purchase summary with standard cash flows
        const purchaseResult = calculatePurchaseSummary();

        // Enhance cash flows with landlord-specific rental income data
        const enhancedCashFlows = enhanceCashFlowsWithLandlordData(purchaseResult, breakEvenRent);

        // Create enhanced purchase summary with landlord cash flows
        const enhancedPurchaseResult = {
          ...purchaseResult,
          monthlyCashFlows: enhancedCashFlows
        };

        // Calculate investment fund for comparison (using the break-even rent)
        const rentalComparison = calculateLandlordInvestmentFund(purchaseResult, breakEvenRent);

        setSummary(enhancedPurchaseResult);
        setRentalAnalysis(rentalComparison); // Store investment fund comparison

        // Save outputs to TylerState
        TylerState.housing.outputs.purchase = {
          ...enhancedPurchaseResult,
          costPerMonth: purchaseResult.monthlyCost,
          costPerYear: purchaseResult.monthlyCost * 12,
          totalNetCost: purchaseResult.costBreakdown.netCost,
          breakEvenRent: breakEvenRent,
          landlordPropertyEquity: purchaseResult.netProceedsFromSale
        };
        TylerState.housing.outputs.rental = {
          ...rentalComparison,
          costPerMonth: breakEvenRent,
          costPerYear: breakEvenRent * 12,
          investmentFundBalance: rentalComparison.finalFundBalance
        };
      }

    // Trigger React updates
    updateReact();

    // Log to console for testing
    debugLog('HOUSING CALCULATOR OUTPUTS', {
      inputs: TylerState.housing.inputs,
      outputs: TylerState.housing.outputs
    });

    setShowResults(true);
    setActiveSection('summary'); // Switch to Summary tab after calculation

    // Auto-save after calculation (if user has a scenario name)
    const currentUser = TylerState.user;
    if (currentUser && TylerState.housingName.trim()) {
      autoSaveScenario();
    }

    // Auto-scroll to results section after calculation while keeping header visible
    setTimeout(() => {
      const resultsSection = document.querySelector('.results-section');
      const header = document.querySelector('.car-planner-header');
      if (resultsSection && header) {
        const headerHeight = header.getBoundingClientRect().height;
        const headerOffset = 20; // Extra padding to ensure header stays visible
        const targetPosition = resultsSection.getBoundingClientRect().top + window.pageYOffset - headerHeight - headerOffset;
        window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
      }
    }, 100);
  };

  const getScenarioCap = (tier: User['tier']) => {
    if (tier === 'supporter') return 1000;
    return 1; // free tier allows 1 scenario, but we'll handle 2 in the UI
  };

  const handleSaveScenario = async (scenarioData?: { name?: string; data?: HousingComparisonData }) => {
    // Wiring this straight to onClick used to hand React's event in here, and the event went on to
    // JSON.stringify in guest storage, which blew up on the circular DOM reference inside it.
    if (scenarioData && (typeof scenarioData !== 'object' || 'nativeEvent' in scenarioData)) {
      scenarioData = undefined;
    }

    const currentUser = TylerState.user;
    if (!currentUser) {
      setPendingSaveAfterLogin(true);
      setGuidanceTitle('Login Required');
      setGuidanceContent('Please create a free account or login to save your scenarios.<br><br>' +
        '<strong>🔐 Create Your Free Account:</strong><br>' +
        '• 💾 Save unlimited scenarios<br>' +
        '• 📱 Access from any device<br>' +
        '• 📊 Track your financial journey<br>' +
        '• 🎯 Personalized recommendations<br>' +
        '• ⭐ Upgrade to Supporter anytime<br><br>' +
        'After logging in, your scenario will be saved automatically.');
      setShowGuidanceModal(true);
      return;
    }
    
    if (!summary) {
      setGuidanceTitle('One more step');
      setGuidanceContent('Please calculate a scenario first so we can save your results.');
      setShowGuidanceModal(true);
      return;
    }

    if (onSaveScenario) {
      onSaveScenario(scenarioData);
    }
    
    setPendingSaveAfterLogin(false); // Clear pending save state
    setGuidanceTitle('Scenario Saved');
    setGuidanceContent(`"${scenarioData?.name || 'Scenario'}" has been saved successfully. You can load it anytime from the scenarios list.`);
    setShowGuidanceModal(true);
  };

  // Effect to handle automatic save after login
  useEffect(() => {
    if (user && pendingSaveAfterLogin && summary && onSaveScenario) {
      // User just logged in and there's a pending save
      // Directly call the save logic to avoid dependency issues
      const scenarioData = {
        name: `Housing Analysis ${new Date().toLocaleDateString()}`,
        data: inputs,
        summary: summary,
        rentalAnalysis: rentalAnalysis,
        createdAt: Date.now()
      };
      
      onSaveScenario(scenarioData);
      setPendingSaveAfterLogin(false); // Clear pending save state
      setGuidanceTitle('Scenario Saved');
      setGuidanceContent(`"${scenarioData.name}" has been saved successfully. You can load it anytime from the scenarios list.`);
      setShowGuidanceModal(true);
    }
  }, [user, pendingSaveAfterLogin, summary, inputs, rentalAnalysis, onSaveScenario]); // Include all dependencies

  // Professional PDF generation
  const generateProfessionalPDF = async () => {
    const pdfService = new HousingPDFService();
    await pdfService.generateProfessionalPDF(
      inputs,
      `Housing Analysis ${new Date().toLocaleString()}`,
      summary,
      rentalAnalysis,
      equivalentHomeValue
    );
  };

  const handleLoadScenario = (scenarioData: HousingComparisonData, scenarioName?: string) => {
    const finalScenarioName = scenarioName || 'Housing Scenario';

    debugLog('📂 LOADING HOUSING DATA', { name: finalScenarioName });

    // Update TylerState (single source of truth)
    TylerState.housingName = finalScenarioName;
    updateReact();

    // Load the scenario data into the main inputs
    // Merge with current state to preserve any new fields with defaults
    setInputs(prev => ({ ...prev, ...scenarioData }));

    // Clear current results and show disclaimer for consistency
    setSummary(null);
    setRentalAnalysis(null);
    setEquivalentHomeValue(null);
    setShowResults(false);
    setSelectedDetailView(null);

    // The disclaimer already gates Calculate; opening it here too just stacks two modals
    // at the same z-index, hiding the one that matters behind this toast.
    setGuidanceTitle('Scenario Loaded');
    setGuidanceContent(`Loaded scenario: ${finalScenarioName}. Click "Calculate Purchase Analysis" to see the results.`);
    setShowGuidanceModal(true);
  };

  // Wrapper function for ScenariosTab component
  const handleLoadScenarioWrapper = (data: BaseScenarioData, scenarioName?: string) => {
    handleLoadScenario(data as HousingComparisonData, scenarioName);
  };

  // Handle section navigation (matching car calculator pattern)
  const handleSectionClick = async (sectionId: string) => {
    // Map results section IDs to detail view states
    const sectionToDetailView: Record<string, 'financing' | 'sale' | 'cashflow' | 'monthly' | 'rental'> = {
      'financing-summary': 'financing',
      'sale-proceeds': 'sale',
      'cash-flow-verification': 'cashflow',
      'monthly-cash-flow': 'monthly',
      'rental-fund-summary': 'rental'
    };

    // If clicking a results section, set the detail view and scroll to results
    if (sectionId in sectionToDetailView) {
      const detailView = sectionToDetailView[sectionId];
      setSelectedDetailView(selectedDetailView === detailView ? null : detailView);
      setActiveSection(sectionId);

      // Scroll to results section
      setTimeout(() => {
        const resultsSection = document.querySelector('.results-section');
        const header = document.querySelector('.car-planner-header');
        if (resultsSection && header) {
          const headerHeight = header.getBoundingClientRect().height;
          const yOffset = headerHeight + 20;
          const y = resultsSection.getBoundingClientRect().top + window.pageYOffset - yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    // Auto-save before switching sections (same pattern as car calculator)
    const currentUser = TylerState.user;

    if (currentUser && TylerState.housingName.trim()) {
      try {
        const name = (TylerState.housingName || '').trim();
        const finalName = name || 'Temporary';

        const cap = getScenarioCap(currentUser.tier || 'free');

        // Get existing scenarios to check if updating
        const scenariosArray = await housingScenariosService.getHousingScenariosArray();
        const existingScenario = scenariosArray.find(s => s.name.toLowerCase() === finalName.toLowerCase());

        if (existingScenario || scenariosArray.length < cap) {
          const updatedData = inputs;

          if (existingScenario) {
            // Update existing scenario
            await housingScenariosService.updateHousingScenario(existingScenario.id, {
              name: finalName,
              description: '',
              data: updatedData
            });
            console.log('✅ Auto-saved on section change:', finalName);
          } else {
            // Create new scenario
            await housingScenariosService.saveHousingScenario({
              name: finalName,
              description: '',
              data: updatedData
            });
            console.log('✅ Auto-saved on section change (new):', finalName);
          }
        }
      } catch (error) {
        console.error('❌ Auto-save on section change failed:', error);
      }
    }

    // Then switch section
    if (activeSection === sectionId) {
      setIsInputSectionVisible(!isInputSectionVisible);
    } else {
      setActiveSection(sectionId);
      setIsInputSectionVisible(true);
    }

    // Auto-scroll to top on section change
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle detail view clicks - coordinates with navigation tabs
  const handleDetailViewClick = (detailView: 'financing' | 'sale' | 'cashflow' | 'monthly' | 'rental') => {
    // Map detail views to section IDs
    const detailToSectionId: Record<string, string> = {
      'financing': 'financing-summary',
      'sale': 'sale-proceeds',
      'cashflow': 'cash-flow-verification',
      'monthly': 'monthly-cash-flow',
      'rental': 'rental-fund-summary'
    };

    // Toggle the detail view
    const newDetailView = selectedDetailView === detailView ? null : detailView;
    setSelectedDetailView(newDetailView);

    // Update active section to match (or clear if toggling off)
    if (newDetailView) {
      setActiveSection(detailToSectionId[detailView]);
    }
  };

  // Dynamic Next Button Component
  const NextButton = () => {
    // Check if we're in a results section
    const isInResultsSection = showResults && resultsSection.some(s => s.id === activeSection);

    // Use resultsSection if we're viewing results, otherwise use input sections
    const sectionsToUse = isInResultsSection ? resultsSection : sections;
    const currentIndex = sectionsToUse.findIndex(s => s.id === activeSection);
    const nextSection = currentIndex < sectionsToUse.length - 1 ? sectionsToUse[currentIndex + 1] : null;

    if (!nextSection && !showResults) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={handleCalculate}
            className="run-simulation-btn primary"
          >
            Calculate Purchase Analysis
          </button>
        </div>
      );
    }

    if (nextSection) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={() => handleSectionClick(nextSection.id)}
            className="run-simulation-btn primary"
          >
            Next: {nextSection.label} →
          </button>
        </div>
      );
    }

    return null;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Local wrapper that uses the shared formatting function
  const formatCurrencyWithColor = (value: number, colorType: 'positive' | 'negative' | 'neutral' = 'neutral') => {
    return formatCurrencyWithColorShared(value, colorType, formatCurrency);
  };

  const getWaterfallValue = (totalValue: number) => {
    if (waterfallView === 'monthly') {
      const totalMonths = getTotalOwnershipYears() * 12;
      return totalMonths > 0 ? totalValue / totalMonths : 0;
    } else if (waterfallView === 'annual') {
      return totalValue / getTotalOwnershipYears();
    }
    return totalValue;
  };

  // Compare mode verdict: monthly view (equivalent rent vs actual rent) plus the year-by-year net worth race
  const calculateBreakEven = () => {
    if (!summary || !rentalAnalysis || inputs.analysisMode !== 'compare') {
      return null;
    }

    const monthlyRent = inputs.monthlyRent;
    const breakEvenRent = rentalAnalysis.equivalentMonthlyRent;
    const renterAtActualRent = calculateRenterSummary(inputs, summary, true);
    const series = buildNetWorthByYear(inputs, summary, renterAtActualRent);
    const buyingIsCheaper = breakEvenRent <= monthlyRent;
    const breakEvenHoldYears = findBreakEvenHoldYears(summary.closingCosts);
    // Discipline only ever helps the renter, so it can only overturn a renting verdict. When buying
    // wins with a perfect saver it wins with any saver, and there is nothing to caveat.
    const breakEvenDisciplinePercent = buyingIsCheaper ? null : findBreakEvenSavingsDiscipline({
      initialFunding: summary.downpayment + summary.closingCosts,
      ownerMonthlyExpenses: summary.monthlyCashFlows.map(flow => flow.totalMonthlyExpense),
      monthlyRent,
      rentInflationRate: inputs.rentalInflationRate,
      investmentReturnRate: inputs.investmentReturnRate,
      investmentTaxRate: inputs.investmentTaxRate
    }, summary.netProceedsFromSale);
    const ownerFinalNetWorth = summary.netProceedsFromSale;
    const renterFinalNetWorth = renterAtActualRent.finalFundBalance;
    const finalGap = Math.abs(ownerFinalNetWorth - renterFinalNetWorth);
    const largerFinalNetWorth = Math.max(ownerFinalNetWorth, renterFinalNetWorth);

    return {
      type: buyingIsCheaper ? 'buy' as const : 'rent' as const,
      message: buyingIsCheaper
        ? `Your rent is ${formatCurrency(monthlyRent - breakEvenRent)} a month above the break-even rent, so buying comes out ahead`
        : `Your rent is ${formatCurrency(breakEvenRent - monthlyRent)} a month below the break-even rent, so renting comes out ahead`,
      savings: Math.abs(monthlyRent - breakEvenRent),
      breakEvenRent,
      breakEvenHoldYears,
      breakEvenDisciplinePercent,
      series,
      crossover: findCrossover(series),
      ownerFinalNetWorth,
      renterFinalNetWorth,
      finalGap,
      // Inside 10% the verdict would flip on a small change to any single assumption, so say so
      // rather than printing BUY in 3rem and letting the user think it is settled.
      isClose: largerFinalNetWorth > 0 && finalGap / largerFinalNetWorth < 0.1
    };
  };

  /**
   * How long they would have to stay for buying to pay. Looks past the hold period they entered,
   * because when renting wins the useful answer is the hold period that would change the verdict.
   */
  const findBreakEvenHoldYears = (closingCosts: number): number | null => {
    const longInputs = { ...inputs, ownershipYears: Math.max(inputs.ownershipYears, LONG_HORIZON_YEARS) };
    const longOwner = calculateOwnerSummary(longInputs, closingCosts);
    const longRenter = calculateRenterSummary(longInputs, longOwner, true);
    return findBuyerBreakEvenYear(buildNetWorthByYear(longInputs, longOwner, longRenter));
  };

  /** The condition the verdict actually hangs on, which is a life question, not a financial one. */
  const describeHoldCondition = (type: 'buy' | 'rent', breakEvenHoldYears: number | null, years: number) => {
    if (breakEvenHoldYears === null) {
      return type === 'buy' ? `over your ${years} years` : `buying never catches up, even after ${LONG_HORIZON_YEARS} years`;
    }
    if (breakEvenHoldYears <= 1) {
      return 'from year one';
    }
    return type === 'buy'
      ? `if you stay at least ${breakEvenHoldYears} years`
      : `unless you stay at least ${breakEvenHoldYears} years`;
  };

  /** Fill in the four numbers that depend on where you live; leave the user's own choices alone. */
  const handleApplyCityPreset = (preset: CityPreset) => {
    setInputs(previous => ({
      ...previous,
      houseCost: preset.houseCost,
      monthlyRent: preset.monthlyRent,
      propertyTaxPercent: preset.propertyTaxPercent,
      condoFees: preset.condoFees,
      downpaymentAmount: previous.useDownpaymentPercent
        ? preset.houseCost * previous.downpaymentPercent / 100
        : previous.downpaymentAmount
    }));
    setActiveCityPreset(preset.id);
    setShowResults(false);
  };

  const describeLead = (crossover: Crossover, years: number) => {
    if (crossover.winner === 'tie') {
      return `Dead heat after ${years} years`;
    }
    const who = crossover.winner === 'buy' ? 'Buyer' : 'Renter';
    return crossover.leadFromYear <= 1
      ? `${who} ahead for all ${years} years`
      : `${who} takes the lead in year ${crossover.leadFromYear}`;
  };

  // Shared style objects for better readability
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem'
  };


  // Define sections for navigation (matching car calculator pattern)
  const sections = [
    {
      id: 'general',
      label: 'Saved Scenarios',
      render: () => (
        <div className="form-section">
          <HousingSavedScenarios
            user={TylerState.user}
            guestScenario={guestScenario}
            currentData={inputs}
            onLoadScenario={handleLoadScenarioWrapper}
            onSaveScenario={handleSaveScenario}
            onInputChange={handleInputChange}
          />
        </div>
      )
    },
    {
      id: 'analysisType',
      label: 'Analysis Type',
      render: () => (
        <div className="form-section">
          <h3>Start From an Example</h3>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Real scenarios with cited assumptions. Load one, then change anything you like.
          </p>
          <div className="template-cards">
            {HOUSING_TEMPLATES.filter(template => template.data.region === (isUSA ? 'US' : 'Canada')).map(template => (
              <button
                key={template.id}
                type="button"
                className="template-card"
                title={template.source}
                onClick={() => handleLoadScenario(template.data, template.name)}
              >
                <span className="mode-title">{template.name}</span>
                <span className="mode-desc">{template.question}</span>
                <span className="template-source">Source: {template.attribution}</span>
              </button>
            ))}
          </div>

          <h3>Choose Your Analysis Type</h3>
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>
            Select the type of analysis you want to perform
          </p>

          <div className="mode-buttons">
            <button
              className={`mode-btn ${inputs.analysisMode === 'housing-to-rent' ? 'active' : ''}`}
              onClick={() => handleInputChange('analysisMode', 'housing-to-rent')}
              aria-pressed={inputs.analysisMode === 'housing-to-rent'}
            >
              <span className="mode-title">Buy Analysis</span>
              <span className="mode-desc">Find the equivalent rent for a house</span>
            </button>
            <button
              className={`mode-btn ${inputs.analysisMode === 'rent-to-housing' ? 'active' : ''}`}
              onClick={() => handleInputChange('analysisMode', 'rent-to-housing')}
              aria-pressed={inputs.analysisMode === 'rent-to-housing'}
            >
              <span className="mode-title">Rent Analysis</span>
              <span className="mode-desc">Find home value equivalent to your rent</span>
            </button>
            <button
              className={`mode-btn ${inputs.analysisMode === 'compare' ? 'active' : ''}`}
              onClick={() => handleInputChange('analysisMode', 'compare')}
              aria-pressed={inputs.analysisMode === 'compare'}
            >
              <span className="mode-title">Rent vs Buy</span>
              <span className="mode-desc">Compare buying vs renting head-to-head</span>
            </button>
            <button
              className={`mode-btn ${inputs.analysisMode === 'landlord-breakeven' ? 'active' : ''}`}
              onClick={() => handleInputChange('analysisMode', 'landlord-breakeven')}
              aria-pressed={inputs.analysisMode === 'landlord-breakeven'}
            >
              <span className="mode-title">Landlord Break-Even</span>
              <span className="mode-desc">What rent covers all costs (property management, vacancy, taxes)?</span>
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'property',
      label: 'Property Details',
      render: () => (
        <div className="form-section">
          <h3>Property Details</h3>

          {inputs.region === 'Canada' && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Start from a city</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                Fills in the average condo price, two-bedroom rent, property tax rate and condo fees. Change anything after.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {CITY_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleApplyCityPreset(preset)}
                    aria-pressed={activeCityPreset === preset.id}
                    style={{
                      padding: '0.4rem 0.85rem',
                      borderRadius: '999px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      border: activeCityPreset === preset.id ? '2px solid #059669' : '1px solid #d1d5db',
                      backgroundColor: activeCityPreset === preset.id ? '#ecfdf5' : 'white',
                      color: activeCityPreset === preset.id ? '#059669' : '#374151',
                      fontWeight: activeCityPreset === preset.id ? 600 : 400
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
              {activeCityPreset && (() => {
                const preset = CITY_PRESETS.find(item => item.id === activeCityPreset);
                return preset?.note ? (
                  <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: '0.75rem' }}>{preset.note}</div>
                ) : null;
              })()}
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.75rem' }} title={CITY_PRESET_SOURCE}>
                {CITY_PRESET_AS_OF}. These are dated averages, not live listings.
              </div>
            </div>
          )}

          <InteractiveSlider
            config={{
              min: 0,
              max: 50,
              step: 1,
              defaultValue: 10,
              format: 'number',
              label: 'Years You\'ll Own Property',
            }}
            value={inputs.ownershipYears}
            onChange={(value) => handleInputChange('ownershipYears', value)}
            guidanceTitle="Ownership Years"
            guidanceContent={'How many years do you plan to own this property before selling?<br><br>' +
              '<strong>Considerations:</strong><br>' +
              '• Shorter periods (1-5 years): High transaction costs may outweigh benefits<br>' +
              '• Medium periods (5-10 years): Break-even point typically occurs<br>' +
              '• Longer periods (10+ years): More time for appreciation and mortgage paydown<br><br>' +
              '<strong>Tip:</strong> Consider your life plans, job stability, and family growth when selecting this timeframe.'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />
          {inputs.analysisMode !== 'rent-to-housing' && (
            <InteractiveSlider
              config={{
                min: 0,
                max: 10000000,
                step: 10000,
                defaultValue: 500000,
                format: 'currency',
                region: 'Canada',
                label: 'Total Cost of House',
                colorThresholds: {
                  redLow: 0.1,
                  redHigh: 0.9,
                  yellowLow: 0.2,
                  yellowHigh: 0.8
                }
              }}
              value={inputs.houseCost}
              onChange={(value) => handleInputChange('houseCost', value)}
              guidanceTitle="Total House Cost"
              guidanceContent={'What is the total purchase price of the house?<br><br>' +
                '<strong>Include:</strong><br>' +
                '• Purchase price of the property<br>' +
                '• Home inspection fees<br>' +
                '• Appraisal costs<br>' +
                '• Legal fees<br><br>' +
                '<strong>Don\'t Include:</strong><br>' +
                '• Land transfer taxes (calculated separately)<br>' +
                '• Down payment (entered below)<br>' +
                '• Mortgage insurance (calculated automatically)<br><br>' +
                '<strong>Tip:</strong> This should be the final negotiated purchase price shown on your Agreement of Purchase and Sale.'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
                setGuidanceContent(content);
                setShowGuidanceModal(true);
              }}
            />
          )}

          <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Ongoing Ownership Costs</h4>

          <InteractiveSlider
            config={{
              min: 0,
              max: 3,
              step: 0.1,
              defaultValue: 1,
              format: 'percent',
              label: 'Property Tax (Annual %)',
            }}
            value={inputs.propertyTaxPercent}
            onChange={(value) => handleInputChange('propertyTaxPercent', value)}
            guidanceTitle="Property Tax Rate"
            guidanceContent={'Annual property tax as % of home value.<br><br>' +
              '<strong>Typical by Province:</strong><br>' +
              '• BC: 0.5-1.0%<br>' +
              '• AB: 0.6-1.1%<br>' +
              '• ON: 0.7-1.2%<br>' +
              '• QC: 0.5-1.5%<br><br>' +
              '<strong>Default:</strong> 1.0% (good average)'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />

          <InteractiveSlider
            config={{
              min: 0,
              max: 3,
              step: 0.1,
              defaultValue: 1,
              format: 'percent',
              label: 'Maintenance & Repairs (Annual %)',
            }}
            value={inputs.maintenancePercent}
            onChange={(value) => handleInputChange('maintenancePercent', value)}
            guidanceTitle="Maintenance & Repairs"
            guidanceContent={'Annual maintenance as % of home value.<br><br>' +
              '<strong>Rule of Thumb:</strong> 1% per year<br><br>' +
              '• New homes: 0.5-0.7%<br>' +
              '• Older homes: 1.5-2%<br><br>' +
              '<strong>Covers:</strong> HVAC, roof, plumbing, landscaping, appliances'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />

          <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={inputs.condoFees > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleInputChange('condoFees', 300); // Default $300/month
                  } else {
                    handleInputChange('condoFees', 0);
                  }
                }}
                style={{ marginRight: '0.5rem' }}
              />
              {inputs.analysisMode === 'rent-to-housing'
                ? 'The equivalent property would have Condo/HOA fees'
                : 'This property has Condo/HOA fees'}
            </label>
            {inputs.condoFees > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <InteractiveSlider
                  config={{
                    min: 0,
                    max: 2500,
                    step: 25,
                    defaultValue: 300,
                    format: 'currency',
                    region: 'Canada',
                    label: 'Monthly Condo/HOA Fees',
                  }}
                  value={inputs.condoFees}
                  onChange={(value) => handleInputChange('condoFees', value)}
                  guidanceTitle="Condo/HOA Fees"
                  guidanceContent={'Monthly condo/HOA fees.<br><br>' +
                    '<strong>Typical Range:</strong> $200-$800/month<br>' +
                    '<strong>Luxury/Downtown:</strong> $1,200-$2,000+/month<br><br>' +
                    '• Single-family homes: Usually $0<br>' +
                    '• Condos/Townhouses: $200-$800+<br>' +
                    '• Downtown Toronto/Vancouver luxury: $1,200-$2,000+<br><br>' +
                    '<strong>Covers:</strong> Building maintenance, amenities, reserve fund'}
                  onShowGuidance={(title, content) => {
                    setGuidanceTitle(title);
                    setGuidanceContent(content);
                    setShowGuidanceModal(true);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'rental-comparison',
      label: 'Rental Information',
      render: () => (
        <div className="form-section">
          <h3>Rental Information</h3>
          {(inputs.analysisMode === 'rent-to-housing' || inputs.analysisMode === 'compare') && (
            <InteractiveSlider
              config={{
                min: 0,
                max: 50000,
                step: 50,
                defaultValue: 2500,
                format: 'currency',
                region: 'Canada',
                label: 'Monthly Rent',
                colorThresholds: {
                  redLow: 0.1,
                  redHigh: 0.9,
                  yellowLow: 0.2,
                  yellowHigh: 0.8
                }
              }}
              value={inputs.monthlyRent}
              onChange={(value) => handleInputChange('monthlyRent', value)}
              guidanceTitle="Monthly Rent"
              guidanceContent={'What is your current monthly rent or the rent you\'re comparing to?<br><br>' +
                '<strong>Include:</strong><br>' +
                '• Base monthly rent<br>' +
                '• Parking fees (if applicable)<br>' +
                '• Storage fees (if applicable)<br><br>' +
                '<strong>Don\'t Include:</strong><br>' +
                '• Utilities (unless included in rent)<br>' +
                '• Renter\'s insurance<br>' +
                '• Internet/cable<br><br>' +
                '<strong>Tips:</strong><br>' +
                '• Use your current rent for comparison mode<br>' +
                '• For rent-to-housing mode, this determines the equivalent home value<br>' +
                '• Consider annual rent increases in your decision'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
                setGuidanceContent(content);
                setShowGuidanceModal(true);
              }}
            />
          )}
          {(inputs.analysisMode !== 'rent-to-housing' && inputs.analysisMode !== 'compare') && (
            <span>Equivalent rental details to be provided on outputs.  Stay tuned! 
            </span>
          )}
        </div>
      )
    },
    {
      id: 'financing',
      label: 'Financing Options',
      render: () => (
        <div className="form-section">
          <h3>Financing Options</h3>

          {/* Province Selector (Canada only, all modes except rent-to-housing) */}
          {inputs.analysisMode !== 'rent-to-housing' && inputs.region === 'Canada' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>
                Province/Location
              </label>
              <select
                value={buyerLocation}
                onChange={(e) => setBuyerLocation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.95rem',
                  backgroundColor: 'white'
                }}
              >
                <option value="ON">Ontario</option>
                <option value="ON-Toronto">Ontario (Toronto)</option>
                <option value="BC">British Columbia</option>
                <option value="QC">Quebec</option>
                <option value="AB">Alberta (No LTT)</option>
                <option value="SK">Saskatchewan (No LTT)</option>
                <option value="MB">Manitoba</option>
                <option value="NB">New Brunswick</option>
                <option value="NS">Nova Scotia</option>
                <option value="PE">Prince Edward Island</option>
                <option value="NL">Newfoundland</option>
              </select>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#6b7280' }}>
                {useDetailedClosingCosts
                  ? 'Auto-updates land transfer tax in detailed closing costs below'
                  : 'Used for first-time buyer rebates. For exact LTT, enable detailed closing costs below'}
              </p>
            </div>
          )}

          {/* First-Time Home Buyer Toggle (Canada only) */}
          {inputs.analysisMode !== 'rent-to-housing' && inputs.region === 'Canada' && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#dcfce7', borderRadius: '8px', border: '2px solid #16a34a' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', color: '#15803d' }}>
                <input
                  type="checkbox"
                  checked={isFirstTimeBuyer}
                  onChange={(e) => setIsFirstTimeBuyer(e.target.checked)}
                  style={{ marginRight: '0.5rem', width: '18px', height: '18px' }}
                />
                🏠 First-Time Home Buyer
              </label>
              {isFirstTimeBuyer && (
                <div style={{ marginTop: '1rem' }}>
                  {(() => {
                    const ltt = calculateLandTransferTax(inputs.houseCost, buyerLocation.replace('-Toronto', ''), true);
                    return (
                      <div style={{ padding: '0.75rem', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                          <span>Provincial LTT:</span>
                          <span>{formatCurrency(ltt.provincialTax)}</span>
                        </div>
                        {ltt.municipalTax > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                            <span>Toronto Municipal LTT:</span>
                            <span>{formatCurrency(ltt.municipalTax)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#16a34a', fontWeight: '600' }}>
                          <span>First-Time Buyer Rebate:</span>
                          <span>-{formatCurrency(ltt.rebate)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: '700', paddingTop: '0.5rem', borderTop: '1px solid #86efac' }}>
                          <span>Net Land Transfer Tax:</span>
                          <span>{formatCurrency(ltt.netTax)}</span>
                        </div>
                        {ltt.rebate > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#15803d' }}>
                            💡 You save {formatCurrency(ltt.rebate)} with the first-time buyer rebate!
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {!isFirstTimeBuyer && (
                <p style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.85rem', color: '#166534' }}>
                  Check this box if you've never owned a home before - you may qualify for land transfer tax rebates
                </p>
              )}
            </div>
          )}

          {/* US Mortgage Interest Deduction (USA only, not for landlords: rental interest is a Schedule E expense) */}
          {inputs.analysisMode !== 'rent-to-housing' && inputs.analysisMode !== 'landlord-breakeven' && inputs.region === 'US' && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#dbeafe', borderRadius: '8px', border: '2px solid #3b82f6' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', color: '#1e40af' }}>
                <input
                  type="checkbox"
                  checked={useTaxDeductions}
                  onChange={(e) => setUseTaxDeductions(e.target.checked)}
                  style={{ marginRight: '0.5rem', width: '18px', height: '18px' }}
                />
                📋 Show Estimated Tax Savings (informational)
              </label>
              {useTaxDeductions && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                    Your Marginal Tax Rate:
                  </label>
                  <select
                    value={marginalTaxRate}
                    onChange={(e) => handleInputChange('marginalTaxRate', Number(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid #93c5fd',
                      fontSize: '0.95rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="10">10% ($0 - $12,400 single)</option>
                    <option value="12">12% ($12,400 - $50,400 single)</option>
                    <option value="22">22% ($50,400 - $105,700 single)</option>
                    <option value="24">24% ($105,700 - $201,775 single)</option>
                    <option value="32">32% ($201,775 - $256,225 single)</option>
                    <option value="35">35% ($256,225 - $640,600 single)</option>
                    <option value="37">37% (Over $640,600 single)</option>
                  </select>
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px', fontSize: '0.85rem' }}>
                    <strong>Tax Benefits of Homeownership:</strong>
                    <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '0.5rem' }}>
                      <li>Mortgage interest deduction (up to $750K loan)</li>
                      <li>Property tax deduction (SALT cap: $40,000 through 2029)</li>
                      <li>Tax savings shown in results after calculation</li>
                    </ul>
                  </div>
                </div>
              )}
              {!useTaxDeductions && (
                <p style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.85rem', color: '#1e40af' }}>
                  Check this box to see the tax saved by itemizing mortgage interest and property tax above the standard deduction. Informational only; it does not change the rent-vs-buy result.
                </p>
              )}
            </div>
          )}

          {/* Detailed/Simple Closing Costs Toggle - Only show for Buy Analysis */}
          {inputs.analysisMode !== 'rent-to-housing' && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '500' }}>
                <input
                  type="checkbox"
                  checked={useDetailedClosingCosts}
                  onChange={(e) => {
                    setUseDetailedClosingCosts(e.target.checked);
                    if (!e.target.checked) {
                      // Reset to simple mode - calculate total as percentage
                      const total = Object.values(detailedClosingCosts).reduce((sum, val) => sum + val, 0);
                      const percentage = (total / inputs.houseCost) * 100;
                      handleInputChange('closingCostsPercent', Math.round(percentage * 10) / 10);
                    }
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                Use detailed closing costs breakdown
              </label>
              <p style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
                {useDetailedClosingCosts ? 'Enter individual cost items below' : 'Using simple percentage-based closing costs'}
              </p>
            </div>
          )}

          {!useDetailedClosingCosts ? (
            <InteractiveSlider
              config={{
                min: 0,
                max: 20,
                step: 0.5,
                defaultValue: 3,
                format: 'percent',
                label: 'Closing Costs',
                colorThresholds: {
                  redLow: 0.8,
                  redHigh: 1.0,
                  yellowLow: 0.6,
                  yellowHigh: 0.8
                }
              }}
              value={inputs.closingCostsPercent}
              onChange={(value) => handleInputChange('closingCostsPercent', value)}
              guidanceTitle="Closing Costs"
              guidanceContent={isUSA
                ? 'What percentage of the home price will you pay in closing costs?<br><br>' +
                  '<strong>Typical Closing Costs Include:</strong><br>' +
                  '• Title insurance & search ($500-$2,000)<br>' +
                  '• Attorney/escrow fees ($1,000-$3,000)<br>' +
                  '• Home inspection ($300-$600)<br>' +
                  '• Appraisal fees ($300-$500)<br>' +
                  '• Recording fees ($100-$500)<br>' +
                  '• Transfer taxes (varies by state)<br><br>' +
                  '<strong>Typical Range:</strong> 2-5% of purchase price'
                : 'What percentage of the home price will you pay in closing costs?<br><br>' +
                  '<strong>Typical Closing Costs Include:</strong><br>' +
                  '• Land transfer taxes (1-2% depending on province)<br>' +
                  '• Legal fees ($1,000-$3,000)<br>' +
                  '• Home inspection ($400-$800)<br>' +
                  '• Appraisal fees ($300-$500)<br>' +
                  '• Title insurance ($200-$400)<br>' +
                  '• Survey costs ($1,000-$2,000)<br><br>' +
                  '<strong>Typical Range:</strong> 2-4% of purchase price<br>' +
                  '<strong>First-time buyers:</strong> May qualify for land transfer tax rebates in some provinces.'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />
          ) : (
            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>Detailed Closing Costs</h4>

              <InteractiveSlider
                config={{
                  min: 0,
                  max: 50000,
                  step: 100,
                  defaultValue: 0,
                  format: 'currency',
                  region: 'Canada',
                  label: isUSA ? 'Transfer Taxes' : 'Land Transfer Tax',
                  colorThresholds: { redLow: 0, redHigh: 1, yellowLow: 0, yellowHigh: 0 }
                }}
                value={detailedClosingCosts.landTransferTax}
                onChange={(value) => setDetailedClosingCosts({...detailedClosingCosts, landTransferTax: value})}
                guidanceTitle={isUSA ? 'Transfer Taxes' : 'Land Transfer Tax'}
                guidanceContent={isUSA
                  ? "State and local transfer taxes on property sale. Varies by state (0.1-2% of purchase price)."
                  : "Provincial tax on property transfer. Varies by province (1-2% of purchase price). First-time buyers may qualify for rebates in some provinces."}
                onShowGuidance={(title, content) => { setGuidanceTitle(title); setGuidanceContent(content); setShowGuidanceModal(true); }}
              />

              <InteractiveSlider
                config={{
                  min: 0,
                  max: 10000,
                  step: 100,
                  defaultValue: 2000,
                  format: 'currency',
                  region: 'Canada',
                  label: 'Legal Fees',
                  colorThresholds: { redLow: 0, redHigh: 1, yellowLow: 0, yellowHigh: 0 }
                }}
                value={detailedClosingCosts.legalFees}
                onChange={(value) => setDetailedClosingCosts({...detailedClosingCosts, legalFees: value})}
                guidanceTitle="Legal Fees"
                guidanceContent="Lawyer fees for reviewing contracts, title search, and handling the transaction. Typically $1,000-$3,000."
                onShowGuidance={(title, content) => { setGuidanceTitle(title); setGuidanceContent(content); setShowGuidanceModal(true); }}
              />

              <InteractiveSlider
                config={{
                  min: 0,
                  max: 2000,
                  step: 50,
                  defaultValue: 600,
                  format: 'currency',
                  region: 'Canada',
                  label: 'Home Inspection',
                  colorThresholds: { redLow: 0, redHigh: 1, yellowLow: 0, yellowHigh: 0 }
                }}
                value={detailedClosingCosts.homeInspection}
                onChange={(value) => setDetailedClosingCosts({...detailedClosingCosts, homeInspection: value})}
                guidanceTitle="Home Inspection"
                guidanceContent="Professional inspection to identify potential issues. Typically $400-$800."
                onShowGuidance={(title, content) => { setGuidanceTitle(title); setGuidanceContent(content); setShowGuidanceModal(true); }}
              />

              <InteractiveSlider
                config={{
                  min: 0,
                  max: 1500,
                  step: 50,
                  defaultValue: 400,
                  format: 'currency',
                  region: 'Canada',
                  label: 'Appraisal Fee',
                  colorThresholds: { redLow: 0, redHigh: 1, yellowLow: 0, yellowHigh: 0 }
                }}
                value={detailedClosingCosts.appraisal}
                onChange={(value) => setDetailedClosingCosts({...detailedClosingCosts, appraisal: value})}
                guidanceTitle="Appraisal Fee"
                guidanceContent="Required by lenders to determine home value. Typically $300-$500."
                onShowGuidance={(title, content) => { setGuidanceTitle(title); setGuidanceContent(content); setShowGuidanceModal(true); }}
              />

              <InteractiveSlider
                config={{
                  min: 0,
                  max: 1000,
                  step: 50,
                  defaultValue: 300,
                  format: 'currency',
                  region: 'Canada',
                  label: 'Title Insurance',
                  colorThresholds: { redLow: 0, redHigh: 1, yellowLow: 0, yellowHigh: 0 }
                }}
                value={detailedClosingCosts.titleInsurance}
                onChange={(value) => setDetailedClosingCosts({...detailedClosingCosts, titleInsurance: value})}
                guidanceTitle="Title Insurance"
                guidanceContent="Protects against title defects and ownership disputes. Typically $200-$400."
                onShowGuidance={(title, content) => { setGuidanceTitle(title); setGuidanceContent(content); setShowGuidanceModal(true); }}
              />

              <InteractiveSlider
                config={{
                  min: 0,
                  max: 10000,
                  step: 100,
                  defaultValue: 1500,
                  format: 'currency',
                  region: 'Canada',
                  label: 'Moving Costs',
                  colorThresholds: { redLow: 0, redHigh: 1, yellowLow: 0, yellowHigh: 0 }
                }}
                value={detailedClosingCosts.movingCosts}
                onChange={(value) => setDetailedClosingCosts({...detailedClosingCosts, movingCosts: value})}
                guidanceTitle="Moving Costs"
                guidanceContent="Professional movers, truck rental, packing supplies, and related expenses."
                onShowGuidance={(title, content) => { setGuidanceTitle(title); setGuidanceContent(content); setShowGuidanceModal(true); }}
              />

              <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
                <strong style={{ color: '#1e40af' }}>Total Detailed Closing Costs: </strong>
                <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e40af' }}>
                  ${(Object.values(detailedClosingCosts).reduce((sum, val) => sum + val, 0)).toLocaleString('en-CA', {maximumFractionDigits: 0})}
                </span>
                <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                  ({((Object.values(detailedClosingCosts).reduce((sum, val) => sum + val, 0) / inputs.houseCost) * 100).toFixed(1)}% of purchase price)
                </span>
              </div>
            </div>
          )}

          {/* Down Payment Input Type Toggle */}
          <div className="interactive-slider" style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div className="slider-label">
              <b>Downpayment Type</b>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={inputs.useDownpaymentPercent}
                onChange={(e) => {
                  handleInputChange('useDownpaymentPercent', e.target.checked);
                }}
                style={{ marginRight: '0.5rem' }}
              />
              Use percentage instead of dollar amount
            </label>
            <p style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
              {inputs.useDownpaymentPercent ? 'Enter down payment as % of home price' : 'Enter down payment as dollar amount'}
            </p>
          </div>

          {!inputs.useDownpaymentPercent ? (
            <InteractiveSlider
              config={{
                min: 0,
                max: 5000000,
                step: 5000,
                defaultValue: 100000,
                format: 'currency',
                region: 'Canada',
                label: 'Down Payment Amount',
                colorThresholds: {
                  redLow: 0.0,
                  redHigh: 0.2,
                  yellowLow: 0.2,
                  yellowHigh: 0.4
                }
              }}
              value={inputs.downpaymentAmount}
              onChange={(value) => {
                handleInputChange('downpaymentAmount', value);
                handleInputChange('downpaymentPercent', (value / inputs.houseCost) * 100);
              }}
              guidanceTitle="Down Payment"
              guidanceContent={isUSA
                ? 'How much cash will you put down to purchase the home?<br><br>' +
                  '<strong>US Down Payment Options:</strong><br>' +
                  '• FHA Loans: 3.5% minimum<br>' +
                  '• Conventional: 5-20% typical<br>' +
                  '• VA Loans: 0% for veterans<br><br>' +
                  '<strong>Benefits of 20%+ Down:</strong><br>' +
                  '• No PMI (Private Mortgage Insurance)<br>' +
                  '• Lower monthly payments<br>' +
                  '• Better mortgage rates<br><br>' +
                  '<strong>PMI Cost:</strong> 0.5%-1.5% of loan annually until 20% equity'
                : 'How much cash will you put down to purchase the home?<br><br>' +
                  '<strong>Canadian Minimum Down Payment (2026):</strong><br>' +
                  '• Homes under $500k: 5% minimum<br>' +
                  '• Homes $500k-$1.5M: 5% on first $500k, 10% on remainder<br>' +
                  '• Homes over $1.5M: 20% minimum<br><br>' +
                  '<strong>CMHC Mortgage Insurance (under 20% down):</strong><br>' +
                  '• 15-19.99% down: 2.80% premium<br>' +
                  '• 10-14.99% down: 3.10% premium<br>' +
                  '• 5-9.99% down: 4.00% premium<br><br>' +
                  '<strong>Mortgage Stress Test:</strong><br>' +
                  'You must qualify at your rate + 2% (min 5.25%)<br><br>' +
                  '<strong>First-Time Buyer Benefits:</strong><br>' +
                  '• 30-year amortization now available<br>' +
                  '• RRSP Home Buyers\' Plan: withdraw up to $60,000<br>' +
                  '• FHSA: tax-free savings for first home'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
                setGuidanceContent(content);
                setShowGuidanceModal(true);
              }}
            />
          ) : (
            <InteractiveSlider
              config={{
                min: 0,
                max: 100,
                step: 1,
                defaultValue: 20,
                format: 'percent',
                label: 'Down Payment Percentage',
                colorThresholds: {
                  redLow: 0.0,
                  redHigh: 0.3,
                  yellowLow: 0.3,
                  yellowHigh: 0.6
                }
              }}
              value={inputs.downpaymentPercent}
              onChange={(value) => {
                handleInputChange('downpaymentPercent', value);
                handleInputChange('downpaymentAmount', (value / 100) * inputs.houseCost);
              }}
              guidanceTitle="Down Payment Percentage"
              guidanceContent={'What percentage of the home price will you pay as a down payment?<br><br>' +
                '<strong>Key Thresholds:</strong><br>' +
                '• 5-19.99%: CMHC insurance required (adds cost)<br>' +
                '• 20%+: No mortgage insurance needed<br>' +
                '• 25%+: Even better rates available<br>' +
                '• 35%+: Best rates and most flexibility<br><br>' +
                '<strong>The 20% Sweet Spot:</strong><br>' +
                'Most experts recommend 20% to avoid CMHC premiums, which can add $10,000+ to your mortgage for a typical home.'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
                setGuidanceContent(content);
                setShowGuidanceModal(true);
              }}
            />
          )}

          {/* CMHC/Mortgage Insurance Warning */}
          {(() => {
            const downPaymentPercent = inputs.useDownpaymentPercent
              ? inputs.downpaymentPercent
              : (inputs.downpaymentAmount / inputs.houseCost) * 100;

            if (downPaymentPercent < 20) {
              const principal = inputs.houseCost - (inputs.useDownpaymentPercent
                ? inputs.houseCost * inputs.downpaymentPercent / 100
                : inputs.downpaymentAmount);

              // CMHC rates based on down payment percentage
              let insuranceRate = 0;
              if (downPaymentPercent >= 15 && downPaymentPercent < 20) {
                insuranceRate = 2.80;
              } else if (downPaymentPercent >= 10 && downPaymentPercent < 15) {
                insuranceRate = 3.10;
              } else if (downPaymentPercent >= 5 && downPaymentPercent < 10) {
                insuranceRate = 4.00;
              } else {
                insuranceRate = 4.50; // < 5% down
              }

              const insurancePremium = principal * (insuranceRate / 100);
              const totalMortgageWithInsurance = principal + insurancePremium;

              return (
                <div style={{
                  marginTop: '1rem',
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                  borderLeft: '6px solid #f59e0b'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e', fontSize: '1rem', fontWeight: '600' }}>
                        Mortgage Insurance Required
                      </h4>
                      <p style={{ margin: '0 0 0.75rem 0', color: '#78350f', fontSize: '0.9rem', lineHeight: '1.5' }}>
                        Down payment below 20% requires mortgage insurance. This protects the lender if you default.
                      </p>
                      <div style={{ backgroundColor: '#fffbeb', padding: '0.75rem', borderRadius: '6px', fontSize: '0.9rem' }}>
                        <div style={{ marginBottom: '0.5rem', color: '#92400e' }}>
                          <strong>Insurance Premium:</strong> {insuranceRate}% of mortgage = <strong>{formatCurrency(insurancePremium)}</strong>
                        </div>
                        <div style={{ color: '#78350f' }}>
                          <strong>Total Mortgage:</strong> {formatCurrency(totalMortgageWithInsurance)} (includes premium added to principal)
                        </div>
                      </div>
                      <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', color: '#a16207' }}>
                        💡 <strong>Tip:</strong> Increasing down payment to 20% ({formatCurrency(inputs.houseCost * 0.20)}) would save you {formatCurrency(insurancePremium)} in insurance costs.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <InteractiveSlider
            config={{
              min: 1,
              max: 50,
              step: 1,
              defaultValue: 25,
              format: 'number',
              label: 'Mortgage Term (Years)',
              colorThresholds: {
                redLow: 0.0,
                redHigh: 0.3,
                yellowLow: 0.3,
                yellowHigh: 0.7
              }
            }}
            value={inputs.mortgageYears}
            onChange={(value) => handleInputChange('mortgageYears', value)}
            guidanceTitle="Mortgage Term"
            guidanceContent={'How many years will it take to pay off your mortgage?<br><br>' +
              '<strong>Shorter Terms (15-20 years):</strong><br>' +
              '• Higher monthly payments<br>' +
              '• Less total interest paid<br>' +
              '• Build equity faster<br>' +
              '• Own home sooner<br><br>' +
              '<strong>Longer Terms (25-30 years):</strong><br>' +
              '• Lower monthly payments<br>' +
              '• More total interest paid<br>' +
              '• More cash flow flexibility<br>' +
              '• Can invest the difference<br><br>' +
              '<strong>Note:</strong> Most Canadian mortgages are amortized over 25 years by default.'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />
          <InteractiveSlider
            config={{
              min: 0,
              max: 25,
              step: 0.1,
              defaultValue: 5.5,
              format: 'percent',
              label: 'Interest Rate',
              colorThresholds: {
                redLow: 0.7,
                redHigh: 1.0,
                yellowLow: 0.5,
                yellowHigh: 0.7
              }
            }}
            value={inputs.interestRate}
            onChange={(value) => handleInputChange('interestRate', value)}
            guidanceTitle="Mortgage Interest Rate"
            guidanceContent={isUSA
              ? 'What is the annual interest rate on your mortgage?<br><br>' +
                '<strong>Current US Rates (2026):</strong><br>' +
                '• 30-year Fixed: 6.0-7.0%<br>' +
                '• 15-year Fixed: 5.5-6.5%<br>' +
                '• FHA Loans: 6.0-6.5%<br>' +
                '• VA Loans: 5.5-6.0%<br><br>' +
                '<strong>Factors That Affect Your Rate:</strong><br>' +
                '• Credit score (740+ gets best rates)<br>' +
                '• Down payment size (20%+ avoids PMI)<br>' +
                '• Loan type (conforming vs jumbo)<br>' +
                '• Points paid at closing<br><br>' +
                '<strong>Tip:</strong> Get pre-approved from multiple lenders to compare offers.'
              : 'What is the annual interest rate on your mortgage?<br><br>' +
                '<strong>Current Canadian Rates (2026):</strong><br>' +
                '• Bank of Canada Rate: declining trend<br>' +
                '• 5-year Fixed: 4.5-5.5%<br>' +
                '• 5-year Variable: 5.0-6.0%<br>' +
                '• 3-year Fixed: 4.0-5.0%<br><br>' +
                '<strong>Mortgage Stress Test:</strong><br>' +
                'You must qualify at rate + 2% (min 5.25%)<br><br>' +
                '<strong>Factors That Affect Your Rate:</strong><br>' +
                '• Credit score (680+ preferred)<br>' +
                '• Down payment size (20%+ avoids CMHC)<br>' +
                '• Employment type & stability<br>' +
                '• GDS/TDS ratios<br><br>' +
                '<strong>Tip:</strong> Use a mortgage broker to compare rates from 30+ lenders.'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />
        </div>
      )
    },
    {
      id: 'financial-rates',
      label: 'Financial Rates',
      render: () => (
        <div className="form-section">
          <h3>Financial Rates & Assumptions</h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            These rates affect the long-term comparison. Smart Canadian defaults are pre-filled, but you can customize them.
          </p>

          <InteractiveSlider
            config={{
              min: 0,
              max: 15,
              step: 0.25,
              defaultValue: 5,
              format: 'percent',
              label: 'Investment Return Rate',
              colorThresholds: {
                redLow: 0,
                redHigh: 0.2,
                yellowLow: 0.2,
                yellowHigh: 0.4
              }
            }}
            value={inputs.investmentReturnRate}
            onChange={(value) => handleInputChange('investmentReturnRate', value)}
            guidanceTitle="Investment Return Rate"
            guidanceContent={'Expected annual return on invested money (like your downpayment invested instead).<br><br>' +
              '<strong>Typical Canadian Returns:</strong><br>' +
              '• High-Interest Savings (HISA): 3-5%<br>' +
              '• GICs: 4-6%<br>' +
              '• Balanced Portfolio: 5-7%<br>' +
              '• Stock Market Average: 7-10%<br><br>' +
              '<strong>Default (5%):</strong> Conservative balanced portfolio assumption.<br><br>' +
              '<strong>Tip:</strong> Higher returns favor renting + investing. Lower returns favor buying.'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />

          <InteractiveSlider
            config={{
              min: 0,
              max: 10,
              step: 0.25,
              defaultValue: 3,
              format: 'percent',
              label: 'Home Appreciation Rate',
              colorThresholds: {
                redLow: 0,
                redHigh: 0.3,
                yellowLow: 0.3,
                yellowHigh: 0.5
              }
            }}
            value={inputs.annualAppreciation}
            onChange={(value) => handleInputChange('annualAppreciation', value)}
            guidanceTitle="Home Appreciation Rate"
            guidanceContent={'Expected annual home value growth rate.<br><br>' +
              '<strong>Historical Canadian Averages:</strong><br>' +
              '• Toronto/Vancouver: 4-6% (long-term)<br>' +
              '• Other Major Cities: 3-5%<br>' +
              '• Rural Areas: 1-3%<br>' +
              '• National Average: ~3%<br><br>' +
              '<strong>Default (3%):</strong> Conservative national average.<br><br>' +
              '<strong>Tip:</strong> Higher appreciation favors buying. Lower appreciation favors renting.'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />

          <InteractiveSlider
            config={{
              min: 0,
              max: 10,
              step: 0.25,
              defaultValue: 3,
              format: 'percent',
              label: 'Rental Inflation Rate',
              colorThresholds: {
                redLow: 0.6,
                redHigh: 1.0,
                yellowLow: 0.4,
                yellowHigh: 0.6
              }
            }}
            value={inputs.rentalInflationRate}
            onChange={(value) => handleInputChange('rentalInflationRate', value)}
            guidanceTitle="Rental Inflation Rate"
            guidanceContent={'Expected annual rent increase rate.<br><br>' +
              '<strong>Canadian Rental Market:</strong><br>' +
              '• Rent Control Provinces: 1-2.5% (capped)<br>' +
              '• Open Market Areas: 3-5%<br>' +
              '• High-Demand Cities: 4-6%<br>' +
              '• National Average: ~3%<br><br>' +
              '<strong>Default (3%):</strong> Matches general inflation.<br><br>' +
              '<strong>Tip:</strong> Higher rental inflation favors buying (rent gets expensive). Lower rental inflation favors renting.'}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />

          {inputs.analysisMode !== 'landlord-breakeven' && (
            <InteractiveSlider
              config={{
                min: 0,
                max: 100,
                step: 5,
                defaultValue: 100,
                format: 'percent',
                label: 'Renter Savings Discipline',
                colorThresholds: {
                  redLow: 0.5,
                  redHigh: 1.0,
                  yellowLow: 0.8,
                  yellowHigh: 1.0
                }
              }}
              value={inputs.renterSavingsDisciplinePercent ?? 100}
              onChange={(value) => handleInputChange('renterSavingsDisciplinePercent', value)}
              guidanceTitle="Renter Savings Discipline"
              guidanceContent={'When renting is cheaper than owning each month, this is the share of that saving the renter actually invests. The rest gets spent.<br><br>' +
                '<strong>Default (100%):</strong> The renter invests every dollar of the difference, every month, for the whole period. This is the textbook assumption and the one most calculators make.<br><br>' +
                '<strong>Why lower it:</strong> A mortgage is forced savings. A renter has to move the money by hand, and most do not. Be honest about yourself.<br><br>' +
                '<strong>What it changes:</strong> The renter\'s investment fund grows more slowly, so the Net Worth Race and the equivalent ownership cost both shift toward buying. Months where rent costs more than owning are unaffected, since the rent has to be paid either way. The down payment and closing costs are always invested in full.<br><br>' +
                '<strong>Tip:</strong> Try 50% to see how much of the case for renting depends on discipline.'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
                setGuidanceContent(content);
                setShowGuidanceModal(true);
              }}
            />
          )}

          {inputs.analysisMode !== 'landlord-breakeven' && (
            <InteractiveSlider
              config={{
                min: 0,
                max: 10,
                step: 0.25,
                defaultValue: 3,
                format: 'percent',
                label: 'Cost Inflation Rate',
                colorThresholds: {
                  redLow: 0.6,
                  redHigh: 1.0,
                  yellowLow: 0.4,
                  yellowHigh: 0.6
                }
              }}
              value={inputs.inflationRate}
              onChange={(value) => handleInputChange('inflationRate', value)}
              guidanceTitle="Cost Inflation Rate"
              guidanceContent={'How fast the owner\'s costs rise each year: property tax, maintenance, insurance and condo fees.<br><br>' +
                '<strong>Default (3%):</strong> Roughly general inflation.<br><br>' +
                '<strong>Why it is separate from rent inflation:</strong> Rent often rises faster than general inflation. ' +
                'PWL Capital models rent at about 1% above inflation; UK data shows 3.5% rent growth against 3% costs. ' +
                'Set rent inflation above cost inflation to capture that.<br><br>' +
                '<strong>Tip:</strong> Higher cost inflation favors renting. Higher rent inflation favors buying.'}
              onShowGuidance={(title, content) => {
                setGuidanceTitle(title);
                setGuidanceContent(content);
                setShowGuidanceModal(true);
              }}
            />
          )}

          {inputs.analysisMode === 'landlord-breakeven' && (
            <>
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 20,
                  step: 0.5,
                  defaultValue: 8,
                  format: 'percent',
                  label: 'Property Management Fee',
                  colorThresholds: {
                    redLow: 0.6,
                    redHigh: 1.0,
                    yellowLow: 0.4,
                    yellowHigh: 0.6
                  }
                }}
                value={inputs.propertyManagementFeePercent}
                onChange={(value) => handleInputChange('propertyManagementFeePercent', value)}
                guidanceTitle="Property Management Fee"
                guidanceContent={'Fee charged by property managers (% of monthly rent collected).<br><br>' +
                  '<strong>Canadian Property Management:</strong><br>' +
                  '• Full Service: 8-12%<br>' +
                  '• Tenant Placement Only: 50-100% of one month rent<br>' +
                  '• Self-Managed: 0%<br><br>' +
                  '<strong>Default (8%):</strong> Standard full-service rate.<br><br>' +
                  '<strong>Services Included:</strong><br>' +
                  '• Tenant screening<br>' +
                  '• Rent collection<br>' +
                  '• Maintenance coordination<br>' +
                  '• Legal compliance<br><br>' +
                  '<strong>Tip:</strong> Set to 0% if you plan to self-manage.'}
                onShowGuidance={(title, content) => {
                  setGuidanceTitle(title);
                  setGuidanceContent(content);
                  setShowGuidanceModal(true);
                }}
              />
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 30,
                  step: 1,
                  defaultValue: 5,
                  format: 'percent',
                  label: 'Vacancy Rate',
                  colorThresholds: {
                    redLow: 0.5,
                    redHigh: 1.0,
                    yellowLow: 0.3,
                    yellowHigh: 0.5
                  }
                }}
                value={inputs.vacancyRatePercent ?? 5}
                onChange={(value) => handleInputChange('vacancyRatePercent', value)}
                guidanceTitle="Vacancy Rate"
                guidanceContent={'Expected percentage of time property sits vacant (no tenant, no rent).<br><br>' +
                  '<strong>Canadian Vacancy Rates:</strong><br>' +
                  '• Low-Vacancy Markets (Toronto, Vancouver): 1-5%<br>' +
                  '• Moderate Markets: 5-10%<br>' +
                  '• High-Vacancy Markets: 10-15%<br>' +
                  '• Student Housing: 15-25% (summer vacancies)<br><br>' +
                  '<strong>Default (5%):</strong> Equivalent to ~1 month vacancy per 20 month tenancy cycle.<br>' +
                  '• This assumes tenants stay for ~1.5-2 years on average<br>' +
                  '• 1 month vacant per 20 months = 5% vacancy rate<br>' +
                  '• Or: 6 weeks vacant per 24 months (2 year cycle)<br><br>' +
                  '<strong>Toronto Condo Suggestion:</strong> 3% (well-managed, high-demand area)<br><br>' +
                  '<strong>Includes:</strong><br>' +
                  '• Turnover time between tenants<br>' +
                  '• Cleaning and repairs<br>' +
                  '• Marketing time<br>' +
                  '• Tenant screening<br><br>' +
                  '<strong>Tip:</strong> Higher vacancy rate = higher rent needed to break even.'}
                onShowGuidance={(title, content) => {
                  setGuidanceTitle(title);
                  setGuidanceContent(content);
                  setShowGuidanceModal(true);
                }}
              />
            </>
          )}

          <InteractiveSlider
            config={{
              min: 15,
              max: 55,
              step: 0.5,
              defaultValue: defaultMarginalTaxRate(inputs.region),
              format: 'percent',
              label: 'Marginal Tax Rate'
            }}
            value={marginalTaxRate}
            onChange={(value) => handleInputChange('marginalTaxRate', value)}
            guidanceTitle="Marginal Tax Rate"
            guidanceContent={(inputs.region === 'Canada' ? 'Combined federal + provincial' : 'Combined federal + state') + ' tax on your next dollar of income.<br><br>' +
              '<strong>Used for:</strong><br>' +
              '• Net rental income (landlord mode): fully taxable at this rate<br>' +
              '• Capital gains on an investment property or a taxable investment account: ' +
              (inputs.region === 'Canada'
                ? 'only 50% of the gain is taxable, so the effective rate is half of this<br><br>'
                : `the calculator applies a flat ${US_CAPITAL_GAINS_TAX_RATE}%<br><br>`) +
              (inputs.region === 'Canada'
                ? '<strong>Default (40%):</strong> roughly an Ontario income of $110K to $150K. ' +
                  'Find your exact rate with the <a href="/income-tax">Income Tax Calculator</a>.<br><br>' +
                  '<strong>Note:</strong> The 2024 proposal to raise the inclusion rate to two-thirds was cancelled in 2025. 50% still applies.'
                : '<strong>Default (22%):</strong> the 2026 federal bracket for roughly $50K to $106K of income filing single, or $101K to $211K married filing jointly.')}
            onShowGuidance={(title, content) => {
              setGuidanceTitle(title);
              setGuidanceContent(content);
              setShowGuidanceModal(true);
            }}
          />

          {/* Which taxes apply - Simple Checkboxes */}
          <div className="interactive-slider">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={inputs.investmentTaxRate > 0}
                onChange={(e) => handleInputChange('investmentTaxRate', e.target.checked ? capitalGainsTaxRate(marginalTaxRate, inputs.region) : 0)}
                style={{ marginRight: '0.5rem' }}
              />
              Taxable Investment Account
              &nbsp;
              <button
                type="button"
                className="guidance-btn"
                title="Investment Tax Rate"
                onClick={(e) => {
                  e.stopPropagation();
                  setGuidanceTitle('Investment Tax Rate');
                  setGuidanceContent(inputs.region === 'Canada' ? `
                    <strong>Tax-Free Accounts (TFSA, RRSP):</strong><br/>
                    ✅ 0% tax on investment income<br/>
                    Leave unchecked if using TFSA or RRSP<br/><br/>

                    <strong>Taxable Accounts:</strong><br/>
                    ❌ Subject to capital gains tax<br/>
                    Check this box if using a non-registered account<br/><br/>

                    <strong>Rate Used: ${capitalGainsTaxRate(marginalTaxRate, inputs.region)}%</strong><br/>
                    50% inclusion rate × your ${marginalTaxRate}% marginal tax rate<br/>
                    Assumes returns come as capital gains; interest would be taxed at the full marginal rate<br/><br/>

                    <strong>When it is charged:</strong><br/>
                    Once, on the gain, when the fund is cashed out at the end — not every year as it grows.
                    That matches how the owner's gain is treated, so the two sides compare fairly.
                    Annual distributions from a real ETF would be taxed sooner, so this is a little generous to the renter.<br/><br/>

                    <strong>Tip:</strong> Most Canadians should leave this unchecked (use TFSA/RRSP for long-term investing).
                  ` : `
                    <strong>Tax-Advantaged Accounts (401(k), IRA, Roth):</strong><br/>
                    ✅ 0% tax on investment growth<br/>
                    Leave unchecked if the money would sit in one of these<br/><br/>

                    <strong>Regular Brokerage Account:</strong><br/>
                    ❌ Subject to capital gains tax<br/>
                    Check this box if the down payment would sit in a taxable account<br/><br/>

                    <strong>Rate Used: ${US_CAPITAL_GAINS_TAX_RATE}%</strong><br/>
                    A flat blend of 15-20% long-term capital gains, 3.8% NIIT for higher earners, and state tax<br/><br/>

                    <strong>When it is charged:</strong><br/>
                    Once, on the gain, when the fund is cashed out at the end — not every year as it grows.
                    That matches how the owner's gain is treated. Annual dividends in a real brokerage account
                    would be taxed sooner, so this is a little generous to the renter.<br/><br/>

                    <strong>Tip:</strong> Most buyers should leave this unchecked.
                  `);
                  setShowGuidanceModal(true);
                }}
              >
                ℹ
              </button>
            </label>
            <div>&nbsp;</div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={inputs.taxesPercent > 0}
                onChange={(e) => handleInputChange('taxesPercent', e.target.checked ? capitalGainsTaxRate(marginalTaxRate, inputs.region) : 0)}
                style={{ marginRight: '0.5rem' }}
              />
              Capital Gains Tax on Home Sale
              &nbsp;
              <button
                type="button"
                className="guidance-btn"
                title="Capital Gains Tax Rate"
                onClick={(e) => {
                  e.stopPropagation();
                  setGuidanceTitle('Capital Gains Tax Rate');
                  setGuidanceContent(inputs.region === 'Canada' ? `
                    <strong>Canadian Principal Residence:</strong><br/>
                    ✅ 0% tax (principal residence exemption)<br/>
                    • Must live in property<br/>
                    • Only one property qualifies per year<br/><br/>

                    <strong>Investment Property (Landlord):</strong><br/>
                    ❌ Subject to capital gains tax<br/>
                    • Gain = sale price − selling costs − (purchase price + closing costs)<br/>
                    • 50% of the gain is taxable, at your marginal rate<br/><br/>

                    <strong>Rate Used: ${capitalGainsTaxRate(marginalTaxRate, inputs.region)}%</strong> of the gain<br/>
                    50% inclusion rate × your ${marginalTaxRate}% marginal tax rate<br/><br/>

                    <strong>Example:</strong><br/>
                    • Buy $500k + $15k closing, sell $650k − $32.5k selling costs = $102.5k gain<br/>
                    • Taxable amount: $51.25k (50% inclusion)<br/>
                    • Tax owed at ${marginalTaxRate}% marginal: ${formatCurrency(102500 * capitalGainsTaxRate(marginalTaxRate, inputs.region) / 100)}<br/><br/>

                    <strong>Tip:</strong> Landlord mode turns this on automatically. Leave it unchecked for a principal residence.
                  ` : `
                    <strong>US Primary Residence:</strong><br/>
                    ✅ The first $250,000 of gain ($500,000 married filing jointly) is tax-free if you lived there 2 of the last 5 years<br/>
                    The calculator treats an unchecked box as fully within the exclusion<br/><br/>

                    <strong>Rental Property (Landlord):</strong><br/>
                    ❌ Gain taxed at 15-20% federal, plus 3.8% NIIT for higher earners, depreciation recapture at 25%, and state tax<br/>
                    • Gain = sale price − selling costs − (purchase price + closing costs)<br/><br/>

                    <strong>Rate Used: ${US_CAPITAL_GAINS_TAX_RATE}%</strong> of the gain, as a flat blend of the above<br/><br/>

                    <strong>Example:</strong><br/>
                    • Buy $400k + $12k closing, sell $540k − $32.4k selling costs = $95.6k gain<br/>
                    • Primary residence: $0 (under the $250k exclusion)<br/>
                    • Rental: ${formatCurrency(95600 * US_CAPITAL_GAINS_TAX_RATE / 100)}<br/><br/>

                    <strong>Tip:</strong> Landlord mode turns this on automatically. Leave it unchecked for a primary residence.
                  `);
                  setShowGuidanceModal(true);
                }}
              >
                ℹ
              </button>
            </label>
          </div>
        </div>
      )
    }
  ];

  const resultsSection = showResults ? [
    {
      id: 'summary',
      label: 'Summary',
      render: () => (
        <div className="results-section">
          <div className="results-header">
            <h2>
              {inputs.analysisMode === 'housing-to-rent' && 'Housing Cost → Rent Analysis'}
              {inputs.analysisMode === 'rent-to-housing' && 'Rent → Housing Cost Analysis'}
              {inputs.analysisMode === 'compare' && 'Rent vs Housing Comparison'}
              {inputs.analysisMode === 'landlord-breakeven' && 'Landlord Break-Even Rent'}
            </h2>
            <div className="results-actions">
              <button
                onClick={() => handleSaveScenario({
                  name: `${inputs.region === 'Canada' ? 'Canada' : 'US'} ${formatCurrency(inputs.houseCost)} - ${new Date().toLocaleDateString()}`,
                  data: inputs
                })}
                className="save-btn"
              >
                {user ? '💾 Save Scenario' : '🔒 Login to Save'}
              </button>
              <button onClick={generateProfessionalPDF} className="print-btn print-professional-btn">
                📊 Print to PDF
              </button>
            </div>
          </div>

          {/* QUICK ANSWER CARD - Prominent recommendation for Compare mode */}
          {inputs.analysisMode === 'compare' && rentalAnalysis && (() => {
            const verdict = calculateBreakEven();
            if (!verdict) {
              return null;
            }
            const years = getTotalOwnershipYears();
            return (
            <div style={{
              background: verdict.type === 'buy'
                ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
              padding: '2rem',
              borderRadius: '16px',
              marginBottom: '1.5rem',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                Based on your inputs, you should:
              </div>
              <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                {verdict.type === 'buy' ? '🏠 BUY' : '🏢 RENT'}
              </div>
              <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                {describeHoldCondition(verdict.type, verdict.breakEvenHoldYears, years)}
              </div>
              <div style={{
                fontSize: '0.9rem',
                opacity: 0.85,
                padding: '0.75rem 1.5rem',
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: '8px',
                display: 'inline-block'
              }}>
                {formatCurrency(verdict.finalGap)} more wealth over your {years} years
                {' '}({formatCurrency(discountToTodaysDollars(verdict.finalGap, years, inputs.inflationRate))} in today's money)
                {' '}· renting wins below {formatCurrency(verdict.breakEvenRent)}/month
              </div>
              {verdict.isClose && (
                <div style={{ fontSize: '0.85rem', marginTop: '1rem', opacity: 0.9 }}>
                  ⚠️ This is close. A small change to appreciation, investment return or rent inflation could flip it.
                </div>
              )}
              {typeof verdict.breakEvenDisciplinePercent === 'number' && (
                <div style={{ fontSize: '0.85rem', marginTop: '0.75rem', opacity: 0.9 }}>
                  Only if you invest it: renting wins here while you actually put at least{' '}
                  <strong>{verdict.breakEvenDisciplinePercent}%</strong> of the monthly difference into the market. Below that, buying wins.
                </div>
              )}
            </div>
            );
          })()}

          {/* BIG NUMBER - Result based on analysis mode */}
          {inputs.analysisMode !== 'compare' && (
          <div className="big-result">
            <h3 style={{marginBottom: '0px'}}>
              {inputs.analysisMode === 'housing-to-rent' && `Equivalent Monthly Rent: ${formatCurrency(rentalAnalysis?.equivalentMonthlyRent || 0)}`}
              {inputs.analysisMode === 'rent-to-housing' && `Equivalent Home Value: ${formatCurrency(equivalentHomeValue || 0)}`}
              {inputs.analysisMode === 'landlord-breakeven' && `Break-Even Monthly Rent: ${formatCurrency(equivalentHomeValue || 0)}`}
            </h3>
            {inputs.analysisMode === 'housing-to-rent' && '(first month)'}
            {inputs.analysisMode === 'rent-to-housing' && '(based on monthly rent)'}
            {inputs.analysisMode === 'landlord-breakeven' && '(covers all costs + taxes)'}
          </div>)}

          {/* QUICK ANSWER */}
          {inputs.analysisMode === 'compare' && rentalAnalysis && (() => {
            const verdict = calculateBreakEven();
            if (!verdict) {
              return null;
            }
            return (
              <div className="break-even-analysis">
                <div className={`break-even-result ${verdict.type}`}>
                  <h3>🎯 {verdict.message}</h3>
                  <div className="break-even-details">
                    <div className="break-even-item">
                      <span>Your Rent:</span>
                      <span>{formatCurrency(inputs.monthlyRent)}</span>
                    </div>
                    <div className="break-even-item">
                      <span title="The rent at which renting and buying finish level. Not what owning costs per month — that is All-In Monthly Cost below.">Break-Even Rent:</span>
                      <span>{formatCurrency(rentalAnalysis.equivalentMonthlyRent)}</span>
                    </div>
                    <div className="break-even-item">
                      <span>Monthly Difference:</span>
                      <span className={verdict.type === 'buy' ? 'positive' : 'negative'}>
                        {formatCurrency(verdict.savings)}
                      </span>
                    </div>
                    <div className="break-even-item highlight">
                      <span title="Who has more wealth each year: the buyer's equity if they sold, or the renter's investment fund">Who&apos;s Ahead:</span>
                      <span className="crossover-point">{describeLead(verdict.crossover, getTotalOwnershipYears())}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* NET WORTH RACE: buyer vs renter, year by year */}
          {inputs.analysisMode === 'compare' && summary && rentalAnalysis && (() => {
            const verdict = calculateBreakEven();
            if (!verdict || verdict.series.length === 0) {
              return null;
            }
            const years = verdict.series.length;
            const maxValue = Math.max(1, ...verdict.series.map(point => Math.max(point.ownerNetWorth, point.renterNetWorth)));
            const rowStep = Math.ceil(years / 5);
            const leadYear = verdict.crossover.leadFromYear;
            const rowsToShow = verdict.series.filter((point, index) =>
              index === 0 || (index + 1) % rowStep === 0 || index === years - 1 || point.year === leadYear
            );
            const barWidth = (value: number) => `${(Math.max(0, value) / maxValue) * 100}%`;
            const winnerLabel = verdict.crossover.winner === 'buy' ? 'Buying' : 'Renting';
            const finalGap = Math.abs(verdict.ownerFinalNetWorth - verdict.renterFinalNetWorth);

            return (
              <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>📊 Net Worth Race: {years} Years</h3>
                <p style={{ marginTop: 0, marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
                  Buyer: home value minus selling costs and mortgage balance if sold that year.
                  Renter: investment fund after paying {formatCurrency(inputs.monthlyRent)}/month in rent.
                </p>

                <NetWorthRaceChart
                  series={verdict.series}
                  leadFromYear={leadYear}
                  winner={verdict.crossover.winner}
                  formatCurrency={formatCurrency}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {rowsToShow.map(point => (
                    <div key={point.year} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '70px', fontWeight: '600', fontSize: '0.9rem', textAlign: 'right' }}>
                        Year {point.year}{point.year === leadYear && leadYear > 1 ? ' ★' : ''}
                      </div>
                      {/* Bars sit in a shared fixed-width track so their lengths are comparable; labels get their own column */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ height: '20px', width: barWidth(point.ownerNetWorth), backgroundColor: '#3b82f6', borderRadius: '3px', minWidth: '4px' }}></div>
                          </div>
                          <span style={{ width: '90px', flexShrink: 0, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatCurrency(point.ownerNetWorth)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ height: '20px', width: barWidth(point.renterNetWorth), backgroundColor: '#f59e0b', borderRadius: '3px', minWidth: '4px' }}></div>
                          </div>
                          <span style={{ width: '90px', flexShrink: 0, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatCurrency(point.renterNetWorth)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#ecfdf5', borderRadius: '8px', fontSize: '0.9rem' }}>
                  <strong>📈 Key Insight:</strong> After {years} years the buyer has {formatCurrency(verdict.ownerFinalNetWorth)} and the renter has {formatCurrency(verdict.renterFinalNetWorth)}.
                  {verdict.crossover.winner === 'tie'
                    ? ' It is a dead heat.'
                    : ` ${winnerLabel} finishes ahead by ${formatCurrency(finalGap)}, which is ${formatCurrency(discountToTodaysDollars(finalGap, years, inputs.inflationRate))} in today's money. ${describeLead(verdict.crossover, years)}.`}
                </div>
              </div>
            );
          })()}

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
            {/* Purchase Summary Card */}
            {summary && (
              <div style={{ backgroundColor: '#f0f9ff', padding: '1.5rem', borderRadius: '8px', border: '2px solid #3b82f6' }}>
                <h4 style={{ marginTop: 0, color: '#1e40af' }}>💰 Purchase Overview</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Home Price:</span>
                    <strong>{formatCurrency(inputs.houseCost)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Down Payment:</span>
                    <strong>{formatCurrency(inputs.useDownpaymentPercent ? (inputs.houseCost * inputs.downpaymentPercent / 100) : inputs.downpaymentAmount)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Monthly Mortgage Payment:</span>
                    <strong>{formatCurrency(summary.monthlyMortgagePayment)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span title="Mortgage + property tax + maintenance + insurance + condo fees">All-In Monthly Cost:</span>
                    <strong>{formatCurrency(summary.totalMonthlyExpense)}</strong>
                  </div>
                  {summary.totalMonthlyExpense !== summary.monthlyMortgagePayment && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '-0.25rem' }}>
                      Includes {formatCurrency(summary.totalMonthlyExpense - summary.monthlyMortgagePayment)}/mo in property tax, maintenance, insurance{inputs.condoFees > 0 ? ', condo fees' : ''}
                    </div>
                  )}
                  {inputs.analysisMode === 'compare' && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      This is cash out the door. The break-even rent above is lower because it also counts the equity you build and what your down payment could have earned invested.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* US Tax Deduction Card (USA only) */}
            {inputs.region === 'US' && inputs.analysisMode !== 'landlord-breakeven' && useTaxDeductions && summary && summary.monthlyCashFlows && (
              (() => {
                const taxDeduction = calculateMortgageInterestDeduction(summary.monthlyCashFlows, marginalTaxRate);
                const annualTaxSavings = taxDeduction.totalSavings / getTotalOwnershipYears();
                const monthlyTaxSavings = annualTaxSavings / 12;
                return (
                  <div style={{ backgroundColor: '#fef3c7', padding: '1.5rem', borderRadius: '8px', border: '2px solid #f59e0b' }}>
                    <h4 style={{ marginTop: 0, color: '#92400e' }}>📋 US Tax Deductions (estimate)</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Itemized Deductions (interest + property tax):</span>
                        <strong>{formatCurrency(taxDeduction.totalDeduction)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Standard Deduction (single filer):</span>
                        <strong>{formatCurrency(US_STANDARD_DEDUCTION_SINGLE)}/yr</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Your Tax Rate:</span>
                        <strong>{marginalTaxRate}%</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid #fcd34d' }}>
                        <span><strong>Estimated Tax Savings (above standard deduction):</strong></span>
                        <strong style={{ color: '#16a34a' }}>{formatCurrency(taxDeduction.totalSavings)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#78716c' }}>
                        <span>Avg. Monthly Savings:</span>
                        <span style={{ color: '#16a34a' }}>{formatCurrency(monthlyTaxSavings)}/mo</span>
                      </div>
                      <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '6px', fontSize: '0.8rem', color: '#78716c' }}>
                        💡 Mortgage interest (up to a $750K loan) and property tax (SALT cap $40K through 2029, $10K from 2030), counted only above the single-filer standard deduction. A married couple's standard deduction is double, so their saving is usually smaller. Informational only; not included in the rent-vs-buy result.
                      </div>
                    </div>
                  </div>
                );
              })()
            )}

            {/* Landlord Break-Even Card */}
            {inputs.analysisMode === 'landlord-breakeven' && summary && equivalentHomeValue && (
              <div style={{ backgroundColor: '#dcfce7', padding: '1.5rem', borderRadius: '8px', border: '2px solid #16a34a' }}>
                <h4 style={{ marginTop: 0, color: '#15803d' }}>🏢 Landlord Break-Even Analysis</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span><strong>Required Monthly Rent:</strong></span>
                    <strong style={{ color: '#16a34a', fontSize: '1.1rem' }}>{formatCurrency(equivalentHomeValue)}</strong>
                  </div>
                  <div style={{ padding: '0.75rem 0', borderTop: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: '0.9rem', color: '#166534', marginBottom: '0.5rem' }}><strong>Breakdown:</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span>Monthly Costs:</span>
                      <span>{formatCurrency(summary.totalMonthlyExpense)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span>Property Mgmt Fee ({inputs.propertyManagementFeePercent}%):</span>
                      <span>-{formatCurrency(equivalentHomeValue * (inputs.propertyManagementFeePercent / 100))}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span>Vacancy Loss ({inputs.vacancyRatePercent ?? 5}%):</span>
                      <span>-{formatCurrency(equivalentHomeValue * ((inputs.vacancyRatePercent ?? 5) / 100))}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span>Income Tax (est. 50%):</span>
                      <span>{formatCurrency((equivalentHomeValue - summary.monthlyCashFlows[0].interestPaid - summary.monthlyCashFlows[0].propertyTax - summary.monthlyCashFlows[0].maintenance - summary.monthlyCashFlows[0].homeInsurance - inputs.condoFees - equivalentHomeValue * (inputs.propertyManagementFeePercent / 100)) * 0.5)}</span>
                    </div>
                  </div>
                  <div style={{ padding: '0.75rem 0', borderTop: '1px solid #bbf7d0', fontSize: '0.85rem', color: '#166534' }}>
                    💡 This rent covers mortgage, taxes, maintenance, management fees, vacancy, and income tax
                  </div>
                </div>
              </div>
            )}

            {/* Rental Analysis Card */}
            {rentalAnalysis && inputs.analysisMode !== 'landlord-breakeven' && (
              <div style={{ backgroundColor: '#fef3c7', padding: '1.5rem', borderRadius: '8px', border: '2px solid #f59e0b' }}>
                <h4 style={{ marginTop: 0, color: '#92400e' }}>
                  🏠 Rental Analysis{inputs.analysisMode === 'compare' ? ' at Break-Even Rent' : ''}
                </h4>
                {inputs.analysisMode === 'compare' && (
                  <p style={{ marginTop: '-0.5rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e' }}>
                    The rent at which renting and buying finish level. Your {formatCurrency(inputs.monthlyRent)} rent is compared in the Net Worth Race above.
                  </p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {(inputs.analysisMode === 'housing-to-rent' || inputs.analysisMode === 'compare') && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{inputs.analysisMode === 'compare' ? 'Break-Even Rent:' : 'Equivalent Monthly Rent:'}</span>
                      <strong>{formatCurrency(rentalAnalysis.equivalentMonthlyRent)}</strong>
                    </div>
                  )}
                  {inputs.analysisMode === 'rent-to-housing' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Monthly Rent (Starting):</span>
                      <strong>{formatCurrency(inputs.monthlyRent)}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total Rent Over {inputs.ownershipYears} Years:</span>
                    <strong>{formatCurrency(rentalAnalysis.totalRentalPayments)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Investment Fund Balance:</span>
                    <strong>
                      {formatCurrency(rentalAnalysis.finalFundBalance)}
                      {(inputs.analysisMode === 'housing-to-rent' || inputs.analysisMode === 'compare') && summary &&
                        <span style={{ fontSize: '0.8rem', color: '#059669', marginLeft: '0.5rem' }}>✓ Break-even</span>
                      }
                    </strong>
                  </div>
                </div>
              </div>
            )}

            {/* Net Position Card */}
            {summary && inputs.analysisMode !== 'landlord-breakeven' && (
              <div style={{ backgroundColor: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', border: '2px solid #22c55e' }}>
                <h4 style={{ marginTop: 0, color: '#15803d' }}>📊 Net Position</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Ownership Period:</span>
                    <strong>{inputs.ownershipYears} years</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Estimated Home Value:</span>
                    <strong>{formatCurrency(summary.estimatedSalePrice)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Net Proceeds After Sale:</span>
                    <strong>{formatCurrency(summary.netProceedsFromSale)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid #86efac' }}>
                    <span style={{ fontWeight: 'bold' }}>Net Cash Flow:</span>
                    <strong>
                      {formatCurrencyWithColor(summary.netCashFlow, summary.netCashFlow >= 0 ? 'positive' : 'negative')}
                    </strong>
                  </div>
                </div>
              </div>
            )}

            {/* Landlord Comparison Card - Buy vs Invest */}
            {inputs.analysisMode === 'landlord-breakeven' && summary && rentalAnalysis && (
              <div style={{ backgroundColor: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', border: '2px solid #22c55e', gridColumn: '1 / -1' }}>
                <h4 style={{ marginTop: 0, color: '#15803d' }}>⚖️ Break-Even Comparison: Buy vs Invest</h4>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
                  At a rent of <strong>{formatCurrency(equivalentHomeValue || 0)}/month</strong>, both scenarios yield the same final position after {inputs.ownershipYears} years:
                </p>

                <div className="comparison-grid" style={{ marginTop: '1rem' }}>
                  {/* Case 1: Buy as Landlord */}
                  <div style={{ backgroundColor: '#dbeafe', padding: '1.25rem', borderRadius: '8px', border: '2px solid #3b82f6' }}>
                    <h5 style={{ marginTop: 0, color: '#1e40af' }}>🏠 Case 1: Buy as Landlord</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Initial Downpayment:</span>
                        <strong>{formatCurrency(inputs.useDownpaymentPercent ? (inputs.houseCost * inputs.downpaymentPercent / 100) : inputs.downpaymentAmount)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Monthly Landlord Cash Flow:</span>
                        <span style={{ fontSize: '0.85rem' }}>Rent - Expenses - Tax</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Property Value After {inputs.ownershipYears} Yrs:</span>
                        <strong>{formatCurrency(summary.estimatedSalePrice)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Remaining Mortgage:</span>
                        <strong>-{formatCurrencyWithColor(summary.remainingMortgageBalance, 'negative')}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #3b82f6', fontWeight: 'bold' }}>
                        <span>Final Property Equity:</span>
                        <strong style={{ fontSize: '1.1rem' }}>{formatCurrencyWithColor(summary.netProceedsFromSale, 'positive')}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Case 2: Invest Instead */}
                  <div style={{ backgroundColor: '#fef3c7', padding: '1.25rem', borderRadius: '8px', border: '2px solid #f59e0b' }}>
                    <h5 style={{ marginTop: 0, color: '#92400e' }}>📈 Case 2: Invest Instead</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Initial Investment (down payment + closing costs):</span>
                        <strong>{formatCurrency(summary.downpayment + summary.closingCosts)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Monthly Additions/Withdrawals:</span>
                        <span style={{ fontSize: '0.85rem' }}>Same as landlord cash flow</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Investment Return Rate:</span>
                        <strong>{inputs.investmentReturnRate}% annually</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Investment Income Earned:</span>
                        <strong>+{formatCurrencyWithColor(rentalAnalysis.totalInvestmentIncome, 'positive')}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #f59e0b', fontWeight: 'bold' }}>
                        <span>Final Investment Balance:</span>
                        <strong style={{ fontSize: '1.1rem' }}>{formatCurrencyWithColor(rentalAnalysis.finalFundBalance, 'positive')}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#ecfdf5', borderRadius: '8px', fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><strong>Difference:</strong></span>
                    <strong style={{ fontSize: '1.1rem' }}>
                      {formatCurrencyWithColor(
                        Math.abs(summary.netProceedsFromSale - rentalAnalysis.finalFundBalance),
                        Math.abs(summary.netProceedsFromSale - rentalAnalysis.finalFundBalance) < 1000 ? 'positive' : 'negative'
                      )}
                      {Math.abs(summary.netProceedsFromSale - rentalAnalysis.finalFundBalance) < 1000 && ' ✓ Break-even!'}
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'financing-summary',
      label: 'Financing Summary',
      render: () => summary ? (
        <div className="results-section">
          <h3>Financing Summary</h3>
          <div style={{ padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Financing Required:</span>
                <strong>{formatCurrency(summary.totalFinancingRequired)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Mortgage Amount:</span>
                <strong>{formatCurrency(summary.totalMortgageValue)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Monthly Mortgage Payment:</span>
                <strong>{formatCurrency(summary.monthlyMortgagePayment)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Monthly Expense:</span>
                <strong>{formatCurrency(summary.totalMonthlyExpense)}</strong>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Run calculation to see results.</div>
      )
    },
    {
      id: 'sale-proceeds',
      label: 'Sale Proceeds',
      render: () => summary ? (
        <div className="results-section">
          <h3>Sale Proceeds (After {inputs.ownershipYears} Years)</h3>
          <div style={{ padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Estimated Sale Price:</span>
                <strong>{formatCurrency(summary.estimatedSalePrice)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Selling Costs:</span>
                <strong>-{formatCurrencyWithColor(summary.sellingCosts, 'negative')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Capital Gains Tax{inputs.taxesPercent === 0 ? ' (principal residence, exempt)' : ` (${inputs.taxesPercent}% of gain)`}:</span>
                <strong>-{formatCurrencyWithColor(summary.taxes, 'negative')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Remaining Mortgage:</span>
                <strong>-{formatCurrencyWithColor(summary.remainingMortgageBalance, 'negative')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '2px solid #d1d5db', fontWeight: 'bold' }}>
                <span>Net Proceeds:</span>
                <strong>
                  {formatCurrencyWithColor(summary.netProceedsFromSale, summary.netProceedsFromSale >= 0 ? 'positive' : 'negative')}
                </strong>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Run calculation to see results.</div>
      )
    },
    {
      id: 'cash-flow-verification',
      label: 'Cash Flow Verification',
      render: () => {
        if (!summary) return (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Run calculation to see results.</div>
        );

        // Everything paid in, less what the sale gives back, should equal the net cost of owning.
        // Read the down payment off the summary rather than the raw input, which is stale whenever
        // the user entered a percentage instead of a dollar amount.
        const downPaymentPlusClosing = summary.downpayment + summary.closingCosts;
        const verification = downPaymentPlusClosing + summary.totalExpensesOverPeriod - summary.netProceedsFromSale;
        const difference = Math.abs(verification - summary.costBreakdown.netCost);
        const isBalanced = difference < 100; // Within $100

        return (
          <div className="results-section">
            <h3>Cash Flow Verification</h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              Every dollar that left your pocket, less what the sale gives back, should equal the net cost of owning.
              If these two figures agree, nothing has been double counted or dropped.
            </p>

            {/* Verification Formula */}
            <div style={{ padding: '1.5rem', backgroundColor: '#f0f9ff', borderRadius: '8px', marginBottom: '1.5rem' }}>
              <h4 style={{ marginTop: 0 }}>Verification Formula</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Down Payment + Closing Costs:</span>
                  <strong>{formatCurrency(downPaymentPlusClosing)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>+ Total Expenses (Mortgage, Taxes, Maintenance):</span>
                  <strong>{formatCurrency(summary.totalExpensesOverPeriod)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>- Net Proceeds from Sale:</span>
                  <strong>-{formatCurrencyWithColor(summary.netProceedsFromSale, 'negative')}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '2px solid #3b82f6', fontWeight: 'bold' }}>
                  <span>= Net Cost of Ownership:</span>
                  <strong>{formatCurrency(verification)}</strong>
                </div>
              </div>
            </div>

            {/* Comparison */}
            <div style={{ padding: '1.5rem', backgroundColor: isBalanced ? '#f0fdf4' : '#fef3c7', borderRadius: '8px', border: `2px solid ${isBalanced ? '#22c55e' : '#f59e0b'}` }}>
              <h4 style={{ marginTop: 0, color: isBalanced ? '#15803d' : '#92400e' }}>
                {isBalanced ? '✓ Verification Passed' : '⚠ Verification Warning'}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>From the cost breakdown:</span>
                  <strong>{formatCurrency(summary.costBreakdown.netCost)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>From the cash flows above:</span>
                  <strong>{formatCurrency(verification)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: `2px solid ${isBalanced ? '#22c55e' : '#f59e0b'}` }}>
                  <span>Difference:</span>
                  <strong style={{ color: isBalanced ? '#15803d' : '#92400e' }}>
                    {formatCurrency(difference)} {isBalanced && '(Within $100 - OK)'}
                  </strong>
                </div>
              </div>
            </div>

            {/* Monthly Cost Explanation */}
            <div style={{ padding: '1.5rem', backgroundColor: '#fef3c7', borderRadius: '8px', marginTop: '1.5rem' }}>
              <h4 style={{ marginTop: 0 }}>Monthly Cost Breakdown</h4>
              <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                Monthly Cost represents the average monthly cost of homeownership after accounting for equity buildup and sale proceeds.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Net Cost of Ownership:</span>
                  <strong>{formatCurrency(summary.costBreakdown.netCost)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>÷ Ownership Period (months):</span>
                  <strong>{inputs.ownershipYears * 12}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '2px solid #fbbf24', fontWeight: 'bold' }}>
                  <span>= Monthly Cost:</span>
                  <strong>{formatCurrency(summary.monthlyCost)}</strong>
                </div>
              </div>
            </div>
          </div>
        );
      }
    },
    {
      id: 'ownership-cash-flows',
      label: 'Ownership Cash Flows',
      render: () => summary && summary.monthlyCashFlows ? (
        <div className="results-section">
          <h3>Ownership Cash Flows</h3>

          {/* Landlord Income Summary (only in landlord mode) */}
          {inputs.analysisMode === 'landlord-breakeven' && (
            <div style={{ padding: '1.5rem', backgroundColor: '#f0fdf4', borderRadius: '8px', marginBottom: '1.5rem' }}>
              <h4 style={{ marginTop: 0, marginBottom: '0.25rem', color: '#059669' }}>Rental Income Summary</h4>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
                Every line is cash over the full {inputs.ownershipYears} years. Principal sits on its own line because it
                leaves your pocket like an expense but comes back as equity.
              </p>
              {(() => {
                const total = (pick: (flow: any) => number) =>
                  summary.monthlyCashFlows.reduce((running: number, flow: any) => running + (pick(flow) || 0), 0);

                const grossRent = total(flow => flow.rentalIncome);
                const managementFees = total(flow => flow.propertyManagementFee);
                const vacancyLoss = total(flow => flow.vacancyLoss);
                const netRent = grossRent - managementFees - vacancyLoss;
                const operatingExpenses = total(flow => flow.propertyTax) + total(flow => flow.homeInsurance)
                  + total(flow => flow.maintenance) + total(flow => flow.mortgageInsurance) + total(flow => flow.condoFees);
                const interest = total(flow => flow.interestPaid);
                const rentalIncomeTax = total(flow => flow.rentalIncomeTax);
                const principal = total(flow => flow.principalPaid);
                const netCashFlow = netRent - operatingExpenses - interest - rentalIncomeTax - principal;

                const lines: Array<{ label: string; value: number; tone: 'positive' | 'negative'; rule?: 'subtotal' | 'total'; note?: string }> = [
                  { label: 'Gross Rent Collected', value: grossRent, tone: 'positive' },
                  { label: 'Less: Property Management Fees', value: managementFees, tone: 'negative' },
                  { label: 'Less: Vacancy Loss', value: vacancyLoss, tone: 'negative' },
                  { label: 'Net Rent Received', value: netRent, tone: 'positive', rule: 'subtotal' },
                  { label: 'Less: Operating Expenses', value: operatingExpenses, tone: 'negative', note: 'property tax, insurance, maintenance, condo fees' },
                  { label: 'Less: Mortgage Interest', value: interest, tone: 'negative' },
                  { label: rentalIncomeTax >= 0 ? 'Less: Rental Income Tax' : 'Plus: Rental Loss Refund', value: Math.abs(rentalIncomeTax), tone: rentalIncomeTax >= 0 ? 'negative' : 'positive' },
                  { label: 'Less: Mortgage Principal', value: principal, tone: 'negative', note: 'cash out, but it buys equity rather than paying for anything' },
                  { label: 'Net Landlord Cash Flow', value: netCashFlow, tone: netCashFlow >= 0 ? 'positive' : 'negative', rule: 'total' }
                ];

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {lines.map(line => (
                      <div
                        key={line.label}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          gap: '1rem',
                          paddingTop: line.rule ? '0.75rem' : 0,
                          borderTop: line.rule ? '2px solid #10b981' : 'none',
                          fontWeight: line.rule ? 'bold' : 'normal',
                          fontSize: line.rule === 'total' ? '1.1rem' : '1rem'
                        }}
                      >
                        <span>
                          {line.label}
                          {line.note && (
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#6b7280', fontWeight: 'normal' }}>{line.note}</span>
                          )}
                        </span>
                        <strong>{formatCurrencyWithColor(line.value, line.tone)}</strong>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Expense Summary Totals */}
          <div style={{ padding: '1.5rem', backgroundColor: '#f0f9ff', borderRadius: '8px', marginBottom: '1.5rem' }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#0369a1' }}>Expense Summary</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Interest Paid:</span>
                <strong>{formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.interestPaid, 0), 'negative')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Property Taxes:</span>
                <strong>{formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.propertyTax, 0), 'negative')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Home Insurance:</span>
                <strong>{formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.homeInsurance, 0), 'negative')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Maintenance:</span>
                <strong>{formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.maintenance, 0), 'negative')}</strong>
              </div>
              {inputs.condoFees > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Condo Fees:</span>
                  <strong>{formatCurrencyWithColor(summary.monthlyCashFlows.reduce((total, flow) => total + flow.condoFees, 0), 'negative')}</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '2px solid #dc2626', fontWeight: 'bold' }}>
                <span>Total Cost of Ownership:</span>
                <strong>
                  {formatCurrencyWithColor(
                    summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.interestPaid + flow.propertyTax + flow.homeInsurance + flow.maintenance, 0) +
                    (summary.monthlyCashFlows.reduce((total, flow) => total + flow.condoFees, 0)),
                    'negative'
                  )}
                </strong>
              </div>
            </div>
          </div>

          {/* Monthly Cash Flows Table */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0 }}>Monthly Ownership Breakdown</h4>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  onClick={() => setMonthlyBreakdownView('summary')}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: monthlyBreakdownView === 'summary' ? '#3b82f6' : '#f3f4f6',
                    color: monthlyBreakdownView === 'summary' ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Summary
                </button>
                <button
                  onClick={() => setMonthlyBreakdownView('detail')}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: monthlyBreakdownView === 'detail' ? '#3b82f6' : '#f3f4f6',
                    color: monthlyBreakdownView === 'detail' ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Detail
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Month</th>
                    {monthlyBreakdownView === 'summary' ? (
                      <>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Mortgage Principal</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Mortgage Interest</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Other Expenses</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total Expenses</th>
                        {inputs.analysisMode === 'landlord-breakeven' && (
                          <>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Net Rent</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Taxes</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Net Cash Flow</th>
                          </>
                        )}
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Mortgage Balance</th>
                      </>
                    ) : (
                      <>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Mortgage Payment</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Principal</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Interest</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Property Tax</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Insurance</th>
                        {summary.monthlyCashFlows.some(f => f.mortgageInsurance > 0) && (
                          <th style={{ padding: '0.75rem', textAlign: 'right' }}>{isUSA ? 'PMI' : 'CMHC'}</th>
                        )}
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Maintenance</th>
                        {inputs.condoFees > 0 && (
                          <th style={{ padding: '0.75rem', textAlign: 'right' }}>Condo Fees</th>
                        )}
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total Expense</th>
                        {inputs.analysisMode === 'landlord-breakeven' && (
                          <>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Gross Rent</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Net Rent</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Rental Tax</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Net Cash Flow</th>
                          </>
                        )}
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Remaining Mortgage Balance</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Cumulative Expense</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {/* TOTAL Row */}
                  <tr style={{ backgroundColor: '#f0f9ff', fontWeight: 'bold', borderBottom: '2px solid #3b82f6' }}>
                    <td style={{ padding: '0.75rem' }}>TOTAL</td>
                    {monthlyBreakdownView === 'summary' ? (
                      <>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.principalPaid, 0), 'positive')}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.interestPaid, 0), 'negative')}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(
                            summary.monthlyCashFlows.reduce((sum, flow) =>
                              sum + flow.propertyTax + flow.homeInsurance + flow.mortgageInsurance + flow.maintenance, 0
                            ) + (summary.monthlyCashFlows.reduce((total, flow) => total + flow.condoFees, 0)),
                            'negative'
                          )}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrency(summary.monthlyCashFlows[summary.monthlyCashFlows.length - 1].cumulativeExpense)}
                        </td>
                        {inputs.analysisMode === 'landlord-breakeven' && (
                          <>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.netRentalIncome || 0), 0), 'positive')}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.rentalIncomeTax || 0), 0), 'negative')}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                              {formatCurrencyWithColor(
                                summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.landlordNetCashFlow || 0), 0),
                                (summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.landlordNetCashFlow || 0), 0)) >= 0 ? 'positive' : 'negative'
                              )}
                            </td>
                          </>
                        )}
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>-</td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrency(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.mortgagePayment, 0))}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.principalPaid, 0), 'positive')}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.interestPaid, 0), 'negative')}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.propertyTax, 0), 'negative')}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.homeInsurance, 0), 'negative')}
                        </td>
                        {summary.monthlyCashFlows.some(f => f.mortgageInsurance > 0) && (
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.mortgageInsurance, 0), 'negative')}
                          </td>
                        )}
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow) => sum + flow.maintenance, 0), 'negative')}
                        </td>
                        {inputs.condoFees > 0 && (
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((total, flow) => total + flow.condoFees, 0), 'negative')}
                          </td>
                        )}
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrency(summary.monthlyCashFlows[summary.monthlyCashFlows.length - 1].cumulativeExpense)}
                        </td>
                        {inputs.analysisMode === 'landlord-breakeven' && (
                          <>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.rentalIncome || 0), 0), 'positive')}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.netRentalIncome || 0), 0), 'positive')}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              {formatCurrencyWithColor(summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.rentalIncomeTax || 0), 0), 'negative')}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                              {formatCurrencyWithColor(
                                summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.landlordNetCashFlow || 0), 0),
                                (summary.monthlyCashFlows.reduce((sum, flow: any) => sum + (flow.landlordNetCashFlow || 0), 0)) >= 0 ? 'positive' : 'negative'
                              )}
                            </td>
                          </>
                        )}
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>-</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {formatCurrency(summary.monthlyCashFlows[summary.monthlyCashFlows.length - 1].cumulativeExpense)}
                        </td>
                      </>
                    )}
                  </tr>

                  {/* Month-by-month rows */}
                  {summary.monthlyCashFlows.map((flow) => (
                    <tr key={flow.month} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}>{flow.month}</td>
                      {monthlyBreakdownView === 'summary' ? (
                        <>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.principalPaid, 'positive')}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.interestPaid, 'negative')}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            {formatCurrencyWithColor(
                              flow.propertyTax + flow.homeInsurance + flow.mortgageInsurance + flow.maintenance + (inputs.condoFees || 0),
                              'negative'
                            )}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(flow.totalMonthlyExpense)}</td>
                          {inputs.analysisMode === 'landlord-breakeven' && (
                            <>
                              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor((flow as any).netRentalIncome || 0, 'positive')}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor((flow as any).rentalIncomeTax || 0, 'negative')}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                {formatCurrencyWithColor((flow as any).landlordNetCashFlow || 0, ((flow as any).landlordNetCashFlow || 0) >= 0 ? 'positive' : 'negative')}
                              </td>
                            </>
                          )}
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            {formatCurrencyWithColor(flow.remainingBalance, flow.remainingBalance >= 0 ? 'positive' : 'negative')}
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(flow.mortgagePayment)}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.principalPaid, 'positive')}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.interestPaid, 'negative')}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.propertyTax, 'negative')}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.homeInsurance, 'negative')}</td>
                          {summary.monthlyCashFlows.some(f => f.mortgageInsurance > 0) && (
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.mortgageInsurance, 'negative')}</td>
                          )}
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.maintenance, 'negative')}</td>
                          {inputs.condoFees > 0 && (
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(inputs.condoFees, 'negative')}</td>
                          )}
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(flow.totalMonthlyExpense)}</td>
                          {inputs.analysisMode === 'landlord-breakeven' && (
                            <>
                              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor((flow as any).rentalIncome || 0, 'positive')}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor((flow as any).netRentalIncome || 0, 'positive')}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor((flow as any).rentalIncomeTax || 0, 'negative')}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                {formatCurrencyWithColor((flow as any).landlordNetCashFlow || 0, ((flow as any).landlordNetCashFlow || 0) >= 0 ? 'positive' : 'negative')}
                              </td>
                            </>
                          )}
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            {formatCurrencyWithColor(flow.remainingBalance, flow.remainingBalance >= 0 ? 'positive' : 'negative')}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(flow.cumulativeExpense)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Run calculation to see results.</div>
      )
    },
    {
      id: 'rental-fund-summary',
      label: 'Investment Fund Summary',
      render: () => rentalAnalysis ? (
        <div className="results-section">
          <h3>Investment Fund Summary</h3>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            {inputs.analysisMode === 'landlord-breakeven'
              ? <>This shows what happens if you <strong>don't buy</strong> the property. Instead, you invest your down payment and add/subtract monthly landlord cash flows.</>
              : <>This shows what your down payment and monthly savings would grow to if <strong>invested instead of buying</strong>.</>
            }
          </p>

          {/* Summary Totals */}
          <div style={{ padding: '1.5rem', backgroundColor: '#fef3c7', borderRadius: '8px', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {inputs.analysisMode === 'landlord-breakeven' ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Break-Even Monthly Rent:</span>
                    <strong>{formatCurrency(rentalAnalysis.equivalentMonthlyRent)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total Landlord Cash Flows:</span>
                    <strong>{formatCurrency(rentalAnalysis.totalRentalPayments)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Investment Income Earned:</span>
                    <strong>{formatCurrencyWithColor(rentalAnalysis.totalInvestmentIncome, 'positive')}</strong>
                  </div>
                  {(rentalAnalysis.capitalGainsTax ?? 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span title="Charged once, when the fund is cashed out at the end — not year by year as it grows.">Less: Capital Gains Tax on Cashing Out:</span>
                      <strong>{formatCurrencyWithColor(rentalAnalysis.capitalGainsTax ?? 0, 'negative')}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '2px solid #fbbf24', fontWeight: 'bold' }}>
                    <span>Final Investment Fund Balance:</span>
                    <strong>{formatCurrencyWithColor(rentalAnalysis.finalFundBalance, 'positive')}</strong>
                  </div>
                </>
              ) : (
                <>
                  {(rentalAnalysis.capitalGainsTax ?? 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span title="Charged once, when the fund is cashed out at the end — not year by year as it grows.">Less: Capital Gains Tax on Cashing Out:</span>
                      <strong>{formatCurrencyWithColor(rentalAnalysis.capitalGainsTax ?? 0, 'negative')}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Equivalent Monthly Rent:</span>
                    <strong>{formatCurrency(rentalAnalysis.equivalentMonthlyRent)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total Rental Payments:</span>
                    <strong>{formatCurrency(rentalAnalysis.totalRentalPayments)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Investment Income Earned:</span>
                    <strong>{formatCurrencyWithColor(rentalAnalysis.totalInvestmentIncome, 'positive')}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '2px solid #fbbf24', fontWeight: 'bold' }}>
                    <span>Final Fund Balance:</span>
                    <strong>{formatCurrencyWithColor(rentalAnalysis.finalFundBalance, 'positive')}</strong>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Monthly Fund Flows Table */}
          <div>
            <h4>Monthly Investment Fund Flows</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Month</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Opening Balance</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Initial Funding</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>{inputs.analysisMode === 'landlord-breakeven' ? 'Net Cash Flow' : 'Monthly Funding'}</th>
                    {inputs.analysisMode !== 'landlord-breakeven' && (
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>Rental Payment</th>
                    )}
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Investment Income</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Taxes</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Total Row */}
                  <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderBottom: '2px solid #fbbf24' }}>
                    <td style={{ padding: '0.75rem' }}>TOTAL</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>-</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {formatCurrency(rentalAnalysis.rentalFundFlows.reduce((sum, flow) => sum + flow.initialFunding, 0))}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {(() => {
                        const totalNetCashFlow = rentalAnalysis.rentalFundFlows.reduce((sum, flow) => sum + flow.monthlyFunding, 0);
                        return formatCurrencyWithColor(totalNetCashFlow, totalNetCashFlow >= 0 ? 'positive' : 'negative');
                      })()}
                    </td>
                    {inputs.analysisMode !== 'landlord-breakeven' && (
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        {formatCurrencyWithColor(rentalAnalysis.rentalFundFlows.reduce((sum, flow) => sum + flow.rentalPayment, 0), 'negative')}
                      </td>
                    )}
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {formatCurrencyWithColor(rentalAnalysis.rentalFundFlows.reduce((sum, flow) => sum + flow.investmentIncome, 0), 'positive')}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {formatCurrencyWithColor(rentalAnalysis.capitalGainsTax ?? 0, 'negative')}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                      {formatCurrencyWithColor(rentalAnalysis.finalFundBalance, rentalAnalysis.finalFundBalance >= 0 ? 'positive' : 'negative')}
                    </td>
                  </tr>

                  {/* Month-by-month rows */}
                  {rentalAnalysis.rentalFundFlows.map((flow, flowIndex) => {
                    // Gains are taxed once, on the way out, so the bill lands in the month you sell.
                    const isFinalMonth = flowIndex === rentalAnalysis.rentalFundFlows.length - 1;
                    const taxThisMonth = isFinalMonth ? (rentalAnalysis.capitalGainsTax ?? 0) : 0;
                    const closingBalance = isFinalMonth
                      ? (flow.closingBalanceAfterTax ?? flow.closingBalance)
                      : flow.closingBalance;
                    return (
                    <tr key={flow.month} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}>{flow.month}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        {formatCurrencyWithColor(flow.openingBalance, flow.openingBalance >= 0 ? 'positive' : 'negative')}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(flow.initialFunding)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        {formatCurrencyWithColor(flow.monthlyFunding, flow.monthlyFunding >= 0 ? 'positive' : 'negative')}
                      </td>
                      {inputs.analysisMode !== 'landlord-breakeven' && (
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.rentalPayment, 'negative')}</td>
                      )}
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(flow.investmentIncome, 'positive')}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrencyWithColor(taxThisMonth, 'negative')}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        {formatCurrencyWithColor(closingBalance, closingBalance >= 0 ? 'positive' : 'negative')}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Run calculation to see results.</div>
      )
    }
  ] : [];

  const allSections = [...sections, ...resultsSection];

  // FAQ data based on country - Enhanced for SEO competitiveness
  const canadaFAQs = [
    { question: "Should I rent or buy a house in Canada?", answer: "It depends on your financial situation, lifestyle, and local market conditions. Buying builds equity but requires a large down payment and ongoing maintenance. Renting offers flexibility and lower upfront costs. Our calculator compares the true costs including opportunity cost of invested capital, maintenance, property taxes, and home appreciation." },
    { question: "How does the rent vs buy calculator work?", answer: "Our calculator compares renting versus buying over your expected timeframe. It accounts for down payment, mortgage payments, property taxes, maintenance, insurance, opportunity cost of invested capital, rent increases, and home appreciation to show you which option costs less in your specific situation." },
    { question: "What is the 5% rule for rent vs buy in Canada?", answer: "The 5% rule suggests you should rent if annual rent is less than 5% of a comparable home's purchase price. For a $500,000 home, 5% is $25,000/year or $2,083/month. If you can rent for less, renting and investing the difference may be better. Our calculator provides a more detailed analysis including opportunity cost and appreciation." },
    { question: "What is the CMHC mortgage stress test in Canada?", answer: "The CMHC stress test requires you to qualify at your mortgage rate plus 2%, with a minimum floor of 5.25%. This ensures you can afford payments if rates rise. For a 5% mortgage, you must qualify at 7%. The stress test sets how much a lender will approve, not the rate you actually pay — enter your real rate in the calculator." },
    { question: "What are GDS and TDS ratios for Canadian mortgages?", answer: "GDS (Gross Debt Service) must be under 39% - your housing costs divided by gross income. TDS (Total Debt Service) must be under 44% - all debt payments divided by gross income. Lenders use these to determine how much you can borrow." },
    { question: "Can first-time buyers get 30-year amortization in Canada?", answer: "Yes, as of 2024, first-time home buyers and those purchasing new builds can get 30-year amortization with CMHC-insured mortgages. This lowers monthly payments but increases total interest paid. Our calculator can show you the impact of different amortization periods on your costs." },
    { question: "What land transfer tax rebates are available for first-time buyers?", answer: "Ontario offers up to $4,000 rebate, BC offers full exemption on homes up to $500,000, and Toronto offers an additional $4,475 municipal rebate. Our calculator includes a first-time buyer mode that automatically calculates your rebate based on province and purchase price." },
    { question: "Is it cheaper to rent or buy in Toronto?", answer: "Toronto's high home prices mean renting is often cheaper in the short term (1-5 years), but buying can be better long-term (10+ years) if home values appreciate. Toronto has both provincial and municipal land transfer taxes. Use our calculator with Toronto-specific prices to see what's best for your situation." },
    { question: "Is it cheaper to rent or buy in Vancouver?", answer: "Vancouver has Canada's highest home prices but relatively low property taxes (0.25%). The BC first-time buyer exemption can save up to $8,000. Renting is often cheaper short-term, but Vancouver's land scarcity supports long-term appreciation. Our calculator accounts for BC-specific costs and rebates." },
    { question: "How much house can I afford in Canada?", answer: "Generally, your total housing costs (mortgage, taxes, insurance) shouldn't exceed 32% of your gross monthly income (GDS ratio). With the stress test, you must qualify at a higher rate. These rules set the maximum a lender will approve; our calculator takes a purchase price and shows whether buying at that price beats renting." },
    { question: "What is CMHC mortgage insurance and when is it required?", answer: "CMHC mortgage default insurance is required when your down payment is less than 20%. Premiums range from 2.8% (15-19.99% down) to 4.5% (less than 5% down) and are added to your mortgage. For a $400,000 home with 10% down, insurance costs about $11,160. Our calculator includes CMHC insurance in your total costs." },
    { question: "What is the First-Time Home Buyer Incentive in Canada?", answer: "The First-Time Home Buyer Incentive was discontinued. CMHC stopped accepting applications on March 21, 2024, saying the program was undersubscribed. It had been a shared-equity mortgage providing 5% for existing homes or 10% for new builds, repayable on sale or after 25 years. First-time buyers today should look at the FHSA, the RRSP Home Buyers' Plan (up to $60,000), 30-year amortization on insured mortgages, and provincial land transfer tax rebates instead." },
    { question: "Is GST exempt for first-time home buyers in Canada?", answer: "As of 2025, first-time buyers of newly constructed homes priced up to $1 million may be exempt from the 5% federal GST. This can save up to $50,000 on a new build. Check eligibility requirements as this applies to new construction only, not resale homes." },
    { question: "How much are closing costs when buying a house in Canada?", answer: "Closing costs typically range from 1.5% to 4% of the purchase price. This includes land transfer tax, legal fees, home inspection, title insurance, and appraisal fees. For a $500,000 home, expect $7,500-$20,000 in closing costs. First-time buyers may qualify for land transfer tax rebates. Our calculator factors these costs into the comparison." },
    { question: "What are property taxes in Canada and how do they affect affordability?", answer: "Property taxes vary by municipality but typically range from 0.5% to 2% of your home's value annually. Toronto is about 0.6%, Vancouver 0.25%, Calgary 0.64%, Montreal 0.87%. For a $600,000 home in Toronto, that's $3,600/year. Our calculator includes property taxes in the total cost of ownership." },
    { question: "Is a 5% down payment enough to buy a house in Canada?", answer: "For homes under $500,000, you can put down as little as 5% in Canada. For homes $500,000-$1.5M, you need 5% on the first $500K and 10% on the remainder. Homes over $1.5M require 20% minimum. All down payments under 20% require CMHC insurance. Our calculator shows how different down payment amounts affect your total costs." },
    { question: "When does buying a house make more financial sense than renting?", answer: "Buying typically makes more sense when you plan to stay 5+ years, have a stable income, can afford 20% down payment, and expect moderate home appreciation. Renting is better for flexibility, short-term stays, or when you can earn higher returns investing your down payment elsewhere. Our calculator shows your break-even point." }
  ];

  const usaFAQs = [
    { question: "Should I rent or buy a house in the US?", answer: "It depends on your financial situation, lifestyle, and local market conditions. Buying builds equity but requires a large down payment and ongoing maintenance. Renting offers flexibility and lower upfront costs. Our calculator compares the true costs including opportunity cost of invested capital, maintenance, property taxes, and home appreciation." },
    { question: "How does the rent vs buy calculator work?", answer: "Our calculator compares renting versus buying over your expected timeframe. It accounts for down payment, mortgage payments, property taxes, maintenance, insurance, opportunity cost of invested capital, rent increases, and home appreciation to show you which option costs less in your specific situation." },
    { question: "What is opportunity cost in the rent vs buy decision?", answer: "Opportunity cost represents what you could earn by investing your down payment instead of using it to buy a house. If you rent and invest the money you would have spent on a down payment, that investment grows over time. Our calculator includes this often-overlooked factor in the comparison." },
    { question: "Is it cheaper to rent or buy in New York?", answer: "New York's high home prices mean renting is often cheaper in the short term (1-5 years), but buying can be better long-term (10+ years) if home values appreciate. Use our calculator with NYC-specific prices and property tax rates to see what's best for your timeframe and financial situation." },
    { question: "How much house can I afford in the US?", answer: "Generally, your total housing costs (mortgage, taxes, insurance) shouldn't exceed 28-36% of your gross monthly income. Our calculator helps you determine affordable purchase prices based on your down payment, income, and expected expenses including maintenance and property taxes." },
    { question: "When does buying a house make more financial sense than renting?", answer: "Buying typically makes more sense when you plan to stay 5+ years, have a stable income, can afford 20% down payment, and expect moderate home appreciation. Renting is better for flexibility, short-term stays, or when you can earn higher returns investing your down payment elsewhere." },
    { question: "What are FHA loans and first-time buyer programs in the US?", answer: "FHA loans allow down payments as low as 3.5% with more flexible credit requirements. Many states offer first-time buyer programs with down payment assistance. Conventional loans require higher credit scores but offer better rates. Our calculator helps you compare the true costs including PMI for low down payment scenarios." },
    { question: "How much are closing costs when buying a house in the US?", answer: "Closing costs typically range from 2% to 5% of the purchase price. This includes title insurance, appraisal fees, attorney fees, lender fees, and prepaid items. For a $300,000 home, expect $6,000-$15,000 in closing costs. Our calculator factors these costs into the rent vs buy comparison." },
    { question: "How do US property taxes vary by state?", answer: "Property taxes vary dramatically by state, from 0.28% in Hawaii to over 2% in New Jersey and Illinois. The national average is about 1.1%. For a $400,000 home, that's $4,400/year on average but could range from $1,100 to $8,000+. Our calculator includes property taxes in the total cost of ownership." },
    { question: "What is PMI and how does it affect my mortgage payment?", answer: "Private Mortgage Insurance (PMI) is required when your down payment is less than 20%. It typically costs 0.5%-1.5% of your loan amount annually. For a $300,000 mortgage, that's $1,500-$4,500/year. PMI can be removed once you reach 20% equity. Our calculator shows how different down payment amounts affect your total costs." }
  ];

  return (
    <div className="housing-calculator" onKeyDown={handleKeyDown} tabIndex={-1}>
      {/* SEO Structured Data - Country-specific FAQs */}
      <StructuredData
        type="faq"
        data={{ faqs: isUSA ? usaFAQs : canadaFAQs }}
      />
      <StructuredData
        type="breadcrumb"
        data={{
          items: [
            { name: "Home", url: "https://larryshelp.org" },
            { name: "Rent vs Buy Calculator", url: "https://larryshelp.org/housing" }
          ]
        }}
      />

      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => {
          setShowDisclaimerModal(false);
        }}
        onAccept={handleDisclaimerAccept}
      />

      <GoogleAdInterstitial
        isOpen={showAdModal}
        onClose={() => {
          setShowAdModal(false);
          // Perform calculation after ad closes
          performCalculation();
        }}
      />

      <GuidanceModal
        isOpen={showGuidanceModal}
        onClose={() => setShowGuidanceModal(false)}
        onAccept={() => {
          setShowGuidanceModal(false);
          // If it's a login required modal, trigger the login modal
          if (guidanceTitle === 'Login Required' && !TylerState.user) {
            window.dispatchEvent(new Event('open-login-modal'));
          }
        }}
        title={guidanceTitle}
        content={guidanceContent}
      />
      <div style={{
          maxWidth: '90%',
          margin: '0 auto',
          padding: '2rem 0'
        }}>
        {/* Header */}
        <div className="car-planner-header">
          <h1>Housing: Rent vs Buy Calculator {isUSA ? 'USA' : 'Canada'}</h1>
          <p className="subtitle">
            {isUSA
              ? 'Compare mortgage payments, PMI, property taxes & tax deductions. Find your break-even point.'
              : 'Compare mortgage payments, CMHC insurance, land transfer tax & first-time buyer rebates. Find your break-even point.'
            }
          </p>
          <Link to="/housing-calculator-info" className="learn-more-btn">Learn More</Link>
        </div>

        {/* Main Grid - Navigation + Content */}
        <div style={{width:"100%"}}>
          {/* Content Panel */}
          <div className="content-panel">
            {/* Mobile Navigation */}
            <div className="mobile-nav">
              {allSections.map(section => (
                <button
                  key={section.id}
                  className={`nav-button ${activeSection === section.id ? 'active' : ''} ${
                    resultsSection.some(r => r.id === section.id) ? 'results-nav' : ''
                  }`}
                  onClick={() => handleSectionClick(section.id)}
                >
                  {section.label}
                </button>
              ))}
            </div>
            
            {/* Mobile Actions */}
            <div className="mobile-actions">
              <button 
                className="run-simulation-btn primary" 
                onClick={handleCalculate}
              >
                Calculate Purchase Analysis
              </button>
            </div>

            {/* Current Section */}
            {isInputSectionVisible && (() => {
              const inputSection = sections.find(section => section.id === activeSection);
              return inputSection && inputSection.render ? (
                <div className="calculator-container">
                  <div className="calculator-form">
                    <div>
                      <div style={containerStyle}>
                        {inputSection.render()}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
            {/* Dynamic Next Button */}
            {isInputSectionVisible && <NextButton />}

            {/* Results Tabs - Render active results section */}
            {showResults && (() => {
              const resultSection = resultsSection.find(section => section.id === activeSection);
              return resultSection && resultSection.render ? (
                <div className="calculator-container">
                  {resultSection.render()}
                </div>
              ) : null;
            })()}
          </div>
        </div>

      </div>

      {/* Sticky Calculate Button */}
      {showStickyCalculateButton && !showResults && (
        <div className="sticky-calculate-button">
          <button 
            onClick={handleCalculate}
            className="run-simulation-btn primary"
          >
            Calculate Purchase Analysis
          </button>
        </div>
      )}
    </div>
  );
}

export default HousingCalculator;